import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { useDevice } from './DeviceContext';
import { createBackgroundInterval } from '../utils/backgroundTimer';
import { startKeepAlive, stopKeepAlive } from '../utils/keepAlive';
import { useScreenWakeLock } from '../utils/useScreenWakeLock';
import { armSilentAudio, setSilentAudioPlaying } from '../utils/silentAudioAnchor';
import {
  getMusicKit,
  isAppleMusicAuthorized,
  reconcileAuthIfLying,
  signOutFromAppleMusic,
  hardResetMusicKit,
  unblockExplicitContent,
} from '../utils/musicKitLoader';
import { NETWORK_STATUS_EVENT } from '../utils/axiosSetup';

/**
 * Phase 2 — Apple Music playback engine.
 *
 * MusicKit JS exposes a SINGLE playback engine (Apple DRM enforces one active
 * playback at a time per page). True dual-deck simultaneous output therefore
 * isn't possible — what was previously a YouTube dual-IFrame crossfade is now
 * a simulated transition: a 10s volume fade on the current track followed by
 * an immediate `skipToNextItem()`. The DJ Console deck UI is preserved by
 * mirroring the single active track on whichever slot is "active".
 *
 * Why MusicKit:
 *   - Plays through a same-origin HTML5 <audio> element, so iOS/Android grant
 *     us the same OS-level audio focus they give Spotify and Apple Music —
 *     screen-lock + app-switch survive without throttling.
 *   - Lock-screen and OS media controls work natively (MediaSession bridges).
 */

const MusicContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// ============================================================================
// Crossfade timing — deliberately boring, deliberately stable
// ============================================================================
// Prior iterations layered fast-path branching, prewarm ticks, wash-trigger
// ticks, silent-anchor rewiring, and double-advance guards on top of the
// core fade curve. Each fix landed clean in preview and broke something
// different in production the same day (wash silence, TV showing next-song,
// PWA audio-focus drops). Feb 2026 tuning per host feedback ("long silence
// between songs" reports): choreographed 8-second window that keeps audio
// audible throughout the transition. See FADE_WINDOW_MS / HANDOFF_AT_MS
// constants below for the exact curve.
//
// The rebuild path is deterministic on every platform (Apple hands us the
// same 2-3s DRM load window regardless of what we did before) and — this is
// the important part — it SUPPRESSES MusicKit's ENDED event for the
// outgoing track (because we're calling setQueue not skipToNextItem), which
// means the ENDED handler's own `axios.post(/queue/next)` never double-
// fires. No timestamp guards needed.
// ===== Audible-continuity crossfade (Feb 2026 tuning) =====
// Host-specified curve to eliminate the "long silence between songs" reports:
//
//   T-25..T-23s : PRIMARY prewarm — catalog GET + queue.append + backend lock
//   T-15..T-13s : SECONDARY prewarm retry (fires if primary silent-failed)
//   T-10..T-8s  : BACKUP prewarm retry (final chance before crossfade)
//   T-8s        : crossfade fires + fade-out curve starts
//   T-5s        : hand-off (skipToNextItem fast-path, else direct-swap rebuild)
//   T-3s        : incoming fade-in complete at 100%
//
// Zero-overlap by design (Apple's DRM is single-instance — we can't overlap
// two streams). The prewarm at T-25s / T-15s / T-10s puts the next track in
// MusicKit's queue so the hand-off at T-5s is a pointer move (~200-500ms),
// giving audible playback of the new song well before the fade-in tops out
// at T-3s. If prewarm silent-failed and we hit the rebuild path, Apple's
// ~4s DRM cold-fetch starts at T-5s so audible playback lands around T-1s
// — still tighter than the previous T-1.2 → post-song-end 3-4s of silence.
const FADE_STEP_MS       = 100;
const FADE_WINDOW_MS     = 5000; // total choreography span: T-8s to T-3s
const HANDOFF_AT_MS      = 3000; // 3s into the window = T-5s (outgoing cut, incoming begins)
const FADE_TRIGGER_SEC   = 8;    // arm the crossfade at T-8s
const INCOMING_START_VOL = 0.6;  // new track's initial volume at T-5s

// Apple Music catalog song IDs are short numeric strings (e.g. "1073359419").
const looksLikeAppleId = (id) => typeof id === 'string' && /^\d{6,}$/.test(id);

// Parse the "One or more items could not be resolved: 1831996427, 1832707283, ..."
// error message that Apple's MusicKit setQueue throws when tracks aren't
// available in the user's Apple Music storefront. Returns the array of failed
// numeric IDs (as strings). Empty array if the message isn't in that format.
//
// This is the recovery hook that lets us gracefully prune region-locked
// tracks and re-resolve them via the LIVE user-storefront search, instead
// of hard-failing the whole play attempt.
const parseUnresolvableIds = (msg) => {
  if (!msg || typeof msg !== 'string') return [];
  const m = msg.match(/could not be resolved[:\s]+([0-9,\s]+)/i);
  if (!m) return [];
  return m[1]
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => /^\d{6,}$/.test(s));
};

// ===== Oct 2026 (guest-inject "loads but doesn't play" RCA) =====
// Verify MusicKit actually began producing audio after a queue swap.
// `setQueue()` + `play()` can resolve while playback silently never reaches
// the PLAYING state — observed specifically on the crossfade REBUILD path
// that a guest song-inject forces (MusicKit's prewarmed next item no longer
// matches the backend, so we rebuild instead of skipToNextItem). Without
// this check the ONLY safety net was the +15s overrun watchdog, so the
// requested song "loaded on deck" but sat silent ~19s before the player
// skipped PAST it to the following playlist track (field video, Oct 2026).
//
// Returns true as soon as the playhead is verifiably advancing. It tolerates
// Apple's 4-6s DRM cold-fetch (loading / waiting states keep it polling) and
// only returns false on a genuine stall/stop or on timeout.
const waitForPlaybackStart = async (m, timeoutMs = 6000) => {
  if (!m) return false;
  const PS = (typeof window !== 'undefined' && window.MusicKit?.PlaybackStates) || {};
  const PLAYING = PS.playing ?? 2;
  const started = Date.now();
  let prevT = null;
  while (Date.now() - started < timeoutMs) {
    await new Promise((r) => setTimeout(r, 200));
    let st = -1;
    let t = 0;
    try { st = m.playbackState; t = Number(m.currentPlaybackTime) || 0; }
    catch (_) { continue; }
    if (st === PLAYING) {
      if (t > 0.6) return true;                        // comfortably past the start
      if (prevT !== null && t > prevT + 0.05) return true; // playhead advanced across samples
      prevT = t;
    } else {
      prevT = null; // loading / waiting / paused — reset progress tracking, keep polling
    }
  }
  return false;
};


// ===== Feb 2026 (production stall RCA) =====
// Client-side telemetry beacon fired whenever playback fails to advance
// cleanly. Rate-limited to one report per (reason, minute) so a single
// wedged tab doesn't hammer the endpoint. Fire-and-forget — never awaits,
// never throws, never blocks the ENDED / crossfade path.
const _stallReportLastAt = new Map();
const reportPlaybackStall = (reason, meta = {}) => {
  try {
    const now = Date.now();
    const key = `${reason}::${Math.floor(now / 60000)}`;
    if (_stallReportLastAt.get(key)) return;
    _stallReportLastAt.set(key, now);
    // Garbage-collect old keys to bound memory.
    if (_stallReportLastAt.size > 100) {
      const cutoff = now - 3600000;
      for (const [k, at] of _stallReportLastAt.entries()) {
        if (at < cutoff) _stallReportLastAt.delete(k);
      }
    }
    const payload = {
      reason,
      meta,
      user_agent: (typeof navigator !== 'undefined' && navigator.userAgent) || '',
      is_hidden: (typeof document !== 'undefined' && document.hidden) || false,
      at: new Date().toISOString(),
    };
    axios.post(`${API}/telemetry/playback-stall`, payload).catch(() => { /* noop */ });
  } catch { /* noop */ }
};

export const MusicProvider = ({ children }) => {
  const { user } = useAuth();
  // Item 5 — Multi-Device Orchestration: only the "master" tab may drive
  // playback. Remote tabs get a friendly toast and a "Take Over" pill in
  // the booth banner. We read the ref (not state) so `play()` and
  // `playTracks()` don't need to re-memoize on every device-list update.
  const { isMasterRef, takeover: deviceTakeover, enabled: deviceEnabled } = useDevice();
  const [playerReady, setPlayerReady] = useState(false);
  // Bumped by `emergencyHardRecoverMusicKit()` to force the MusicKit-init
  // effect to re-run — used as the nuclear escalation when the silent-audio
  // watchdog can't unstick the SDK. See the escalation path in the
  // silent-audio watchdog for the full choreography.
  const [musicKitEpoch, setMusicKitEpoch] = useState(0);
  const lastHardMkResetAtRef = useRef(0);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  // Android Chrome's Widevine CDM is occasionally flaky on cheaper tablets
  // mid-set. We DO NOT proactively warn the host any more — the warning
  // fired on every Android session regardless of whether DRM ever actually
  // dropped, becoming pure noise on tablets that worked fine. The silent
  // retry chain below (see "DRM session auto-recovery") handles real drops,
  // and the banner only surfaces after retries are exhausted.

  const [currentTrack, setCurrentTrack] = useState(null);
  // Keep the screen awake whenever a track is loaded — NOT only while
  // `isPlaying` is true. Between every song `isPlaying` briefly flips to
  // false; releasing the wake lock there lets the OS start dimming, which
  // on Android tablets (Lenovo Tab K11 seen in the field 2026-09-03)
  // triggers Chrome's background-tab throttling and stalls the next
  // track. As long as we have a currentTrack, the venue's intent to play
  // is unambiguous → hold the screen. Released when `currentTrack` clears
  // (user stopped) or the MusicProvider unmounts.
  useScreenWakeLock(isPlaying || !!currentTrack);
  // Ref mirror of currentTrack so callbacks (like handleTrackEnded) can
  // read the latest value without stale closures.
  const currentTrackRef = useRef(null);
  useEffect(() => { currentTrackRef.current = currentTrack; }, [currentTrack]);
  const [progressMs, setProgressMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [showPlayer, setShowPlayer] = useState(false);
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [nextUp, setNextUp] = useState(null);
  const [transition, setTransition] = useState(null);
  const [crossfadeActive, setCrossfadeActive] = useState(false);

  // Silent-audio anchor bridge — keep the OS-level audio focus locked
  // whenever we're PLAYING or about to hand off between songs. When the
  // screen turns off on Android (esp. Samsung's aggressive background app
  // policy on Fold 6), any microsecond where no HTML5 <audio> element is
  // "playing" causes the OS to release audio focus. When MusicKit's ENDED
  // fires for the outgoing track it takes a beat for the next track's
  // audio to start — that gap is what kills background playback. The
  // silent anchor humming through that window keeps focus locked so
  // `m.play()` on the next-track hand-off is accepted.
  useEffect(() => {
    setSilentAudioPlaying(isPlaying || crossfadeActive);
  }, [isPlaying, crossfadeActive]);

  // === Live-playback flag for the 12h auto cache reset ===
  // `window.__spinItUpIsLive` is read by registerSW.js's auto-reset
  // safety gate — when true, the reset is deferred 5 minutes and re-checked.
  // Guarantees we never wipe/reload the page mid-song during a live shift.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__spinItUpIsLive = !!(isPlaying || crossfadeActive);
  }, [isPlaying, crossfadeActive]);

  // === Playback state liveness ping (Feb 2026 hours-online fix) ===
  // POST /api/playback/ping every 3s so `playback_state.updated_at` stays
  // fresh REGARDLESS of which admin page the DJ is on. Previously only
  // DJConsolePage pushed state (via PUT /state), so navigating to Requests
  // / Menu / etc while music was still playing would freeze updated_at →
  // /analytics/heartbeat's 15s fallback would miss → `playback_events`
  // stayed empty → HOURS ONLINE stuck at 0 in production. MusicContext is
  // always mounted, so this fires on every page. Only sends when logged in
  // as an admin/host — guests hit the same context but have no session.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Guest surfaces mount MusicProvider too but have no session — skip the
    // ping to avoid a stream of 401s in the network tab.
    if (!user) return;
    let stopped = false;
    const ping = async () => {
      if (stopped) return;
      try {
        await axios.post(
          `${API}/playback/ping`,
          { is_playing: !!(isPlaying || crossfadeActive) },
          { withCredentials: true, timeout: 5000 },
        );
      } catch {
        // Silent — the analytics fallback tolerates missed pings.
      }
    };
    // First ping immediate, then every 3s to match the DJ Console cadence.
    ping();
    const id = setInterval(ping, 3000);
    return () => { stopped = true; clearInterval(id); };
  }, [isPlaying, crossfadeActive, user]);

  // === DJ Console state (single-engine mirror) ===
  const [autoDJ, setAutoDJState] = useState(true);
  const [crossfader, setCrossfaderState] = useState(0.5);
  const [slotATrack, setSlotATrack] = useState(null);
  const [slotBTrack, setSlotBTrack] = useState(null);
  const [slotAState, setSlotAState] = useState('idle');
  const [slotBState, setSlotBState] = useState('idle');
  const [slotAProgress, setSlotAProgress] = useState(0);
  const [slotBProgress, setSlotBProgress] = useState(0);
  const [slotADuration, setSlotADuration] = useState(0);
  const [slotBDuration, setSlotBDuration] = useState(0);

  const musicRef = useRef(null);
  const activeSlotRef = useRef('A');
  const fadeIntervalRef = useRef(null);
  const queueLockRef = useRef(false);
  const autoDJRef = useRef(true);
  const playIntentRef = useRef(false);
  const crossfadeTriggeredForIdRef = useRef(null); // dedupes crossfade trigger per track
  const washTriggeredForIdRef = useRef(null);      // dedupes wash overlay per track
  // Timestamp of the LAST time queueLockRef became true. Feb 8 2026 —
  // production report from Yazu Bangalore of "no crossfade between
  // songs" pointed at a stuck queueLockRef preventing every subsequent
  // T-8s trigger from firing (see the trigger's `!queueLockRef.current`
  // guard). Anything that fails to clear the lock in a `finally` —
  // an unhandled exception in a deep async chain, a `refreshQueue()`
  // rejection, etc — leaks it. The watchdog below force-clears after
  // 30s; this ref just tracks when it last engaged.
  const queueLockSetAtRef = useRef(0);
  const fadeIntervalSetAtRef = useRef(0);
  // Playhead-stall detector state. When a track hits its silent tail some
  // MusicKit builds STOP advancing `currentPlaybackTime` while
  // `playbackState` still reads "playing" (state=2). Reported field case:
  // songs ending 10-12s before their metadata duration, then dead air
  // until the natural ENDED event finally fires. We watch for N
  // consecutive ticks where `cur` hasn't moved AND `remaining` is short
  // enough that this can't be legitimate playback → treat as end-of-audio
  // and force the crossfade early.
  const lastPlayheadCurRef = useRef(0);            // last observed cur
  const stalledTicksRef = useRef(0);               // consecutive stalled tick count
  const stallCrossfadeFiredForIdRef = useRef(null); // dedupe stall-triggered crossfade per track
  // Prewarm state — set to `curId` once MusicKit's queue.length has been
  // OBSERVED to grow after a successful append. Three prewarm windows fire
  // (T-25..T-23 primary, T-15..T-13 secondary, T-10..T-8 backup); each
  // later window only runs if the earlier append didn't verifiably take
  // (MusicKit's `append` can silently no-op on Android Chrome when the DRM
  // session is momentarily busy — a leading cause of the intermittent
  // 4-6s gaps clients still report). The verified retry closes that gap.
  const prewarmSucceededForIdRef = useRef(null);
  // Manual-skip in-flight guard. Rapid forward/rewind clicks on the DJ
  // Console used to race: two `POST /api/queue/next` calls would advance
  // the backend TWICE while two concurrent `setAppleQueue` calls collided
  // in MusicKit (Apple throws "play() without previous stop()" on the
  // second call and the player hangs mid-transition). Setting this ref
  // at the start of `nextTrack` / `previousTrack` and clearing it in the
  // finally block ensures only one manual skip is ever in flight.
  const manualSkipInFlightRef = useRef(false);
  // Timestamp of the last backend `POST /api/queue/next` fired from the
  // crossfade path. Used by the ENDED handler to skip its own advance if
  // the crossfade already did one within the last 8s (otherwise the
  // backend advances TWICE per song and the TV mirrors the wrong track).
  const lastCrossfadeAdvanceAtRef = useRef(0);
  // on Android Chrome, and using it as the sole heuristic caused every
  // crossfade to fall into the slow-path (6-7s DRM reload) even when the
  // queue was actually intact. Field-reported by hosts on cheap Android
  // tablets, Jul 2026.
  //
  // Jul 2026 (later that day): the `queueIntactRef` fast-path itself caused
  // three cascading regressions (silent wash, TV double-advance, random
  // between-song pauses on PWA). Reverted to rebuild-only. This comment
  // block kept as an epitaph so nobody re-introduces the same optimisation.
  const lastEndedAdvanceAtRef = useRef(0);
  // Sep 2026 — "same song repeated" bug reported by Yazu Bangalore:
  // when handleTrackEnded's audio hand-off to the NEXT track fails,
  // we deliberately keep the backend `current_index` pointer put so
  // the retry lands on the same slot. But if the same next-track ID
  // fails 3 times running, we're in a loop — the track itself is
  // broken (Apple 404, DRM licence miss, deleted from catalogue) and
  // no amount of retrying will unstick it. Track consecutive failures
  // per apple_id here; after N in a row we CALL /queue/next anyway
  // (skip past the bad slot) and try the one after.
  const consecutiveHandoffFailsRef = useRef({ appleId: null, count: 0 });

  // Feb 13 2026 — recovery serialization mutex.
  //
  // Code review Feb 13 2026 found the actual root cause of the "no
  // auto-advance / loops previous track" bug (Yazu, Cafe Strokes,
  // Jolly Johnny's Bar). NOT Apple's fault; not the SDK settings; not
  // the specific tracks. Two of OUR OWN recovery paths were racing:
  //
  //   state watchdog @ recoveryAttempts >= 2
  //     ├─ fires handleTrackEnded (unawaited)
  //     └─ ALSO fires hardReset() in the SAME tick
  //
  // handleTrackEnded acquires queueLockRef=true. hardReset never
  // checked it. So both concurrently POST /queue/next + rebuild the
  // MusicKit queue on the same instance. Result: backend advances
  // twice while MusicKit's queue is in an inconsistent state ->
  // MusicKit's error fallback picks the last-successfully-loaded item
  // (the PREVIOUS track) => the "loops previous track" symptom every
  // client has been reporting.
  //
  // Fix: SINGLE serialized recovery. All three recovery entry points
  // (handleTrackEnded, hardReset, emergencyHardRecoverMusicKit) acquire
  // this mutex before doing any work, and NONE of them starts while
  // another is running. State watchdog's ladder becomes strictly
  // sequential: await handleTrackEnded → check state → escalate to
  // hardReset only if still stalled.
  const recoveryInFlightRef = useRef(false);
  // Feb 13 2026 — list of MusicKit event listeners attached in the init
  // effect, so the cleanup can detach every one on re-run.
  const mkListenersRef = useRef([]);
  const acquireRecovery = useCallback((who) => {
    if (recoveryInFlightRef.current) {
      console.info('[recovery] skipping', who, '- another path already in flight');
      return false;
    }
    recoveryInFlightRef.current = true;
    return true;
  }, []);
  const releaseRecovery = useCallback(() => {
    recoveryInFlightRef.current = false;
  }, []);

  // Feb 13 2026 — session-local "poisoned tracks" set.
  //
  // Field observation Feb 13 (Jolly Johnny's Bar, live): song 1 ends,
  // system tries song 2, Apple throws CONTENT_RESTRICTED ("playback
  // failed, please check explicit content settings"). Our outer catch
  // silently swallowed the error, backend pointer stayed at song 1,
  // state watchdog hit hardReset 12-15s later, hardReset replayed
  // song 1 from the beginning. Loop forever.
  //
  // Root cause: my previous "consecutive-fails counter" only fires in
  // handleTrackEnded's `if (!audible)` branch. A THROWN exception from
  // setAppleQueue/play jumps past that branch entirely — the counter
  // never increments, so my skip-after-2-fails logic never fires.
  //
  // Fix: detect CONTENT_RESTRICTED (or any explicitly-unplayable Apple
  // error) in the outer catch, add the offending Apple ID to a
  // session-local Set, force-advance the backend past the broken slot
  // once, and re-fire handleTrackEnded against the new next-track. The
  // Set means we NEVER try that track again in this session — even if
  // the queue somehow lands back on it (retry paths, requests, etc.).
  const poisonedAppleIdsRef = useRef(new Set());
  const isContentRestrictedError = (err) => {
    if (!err) return false;
    const parts = [
      err?.message,
      err?.error?.message,
      err?.error?.description,
      err?.description,
      err?.name,
      err?.error?.name,
      err?.toString?.(),
    ].filter(Boolean).join(' | ');
    return /content.?restrict|explicit.?content|content_restricted|not.*allowed|parental.?control/i.test(parts);
  };

  // Feb 12 2026 — network-flaky state for playback recovery.
  //
  // Field observation (Yazu Bangalore, Feb 12 2026): the "same song loops"
  // failure mode always coincided with the Connectivity Pill going amber
  // ("Reconnecting…"). Root cause: during a WiFi blip on cheap Android
  // tablets, three things fail SIMULTANEOUSLY:
  //   1. Our `axios.get('/queue/current')` inside handleTrackEnded times
  //      out or fails with no response → we can't discover the next track.
  //   2. Apple's DRM manifest fetch for the next-track's audio HLS fails
  //      → MusicKit's `setQueue + play` silently doesn't reach state 2.
  //   3. `POST /queue/next` fails → backend pointer never advances.
  //
  // Then when the network restores, our state watchdog fires hardReset →
  // reads a stale `current_index` → replays the previous track. To the DJ
  // it looks like "the song looped." To us it looks like a broken track
  // and we'd wrongly skip it under the count-≥2 rule.
  //
  // Fix strategy: whenever axios emits `reconnecting` or `offline`, we
  // set `networkFlakyRef` to true with a 15 s tail (long enough for DRM
  // handshakes to stabilise after connectivity returns). While flaky:
  //   • Handoff failures DO NOT count toward the broken-track threshold.
  //   • hardReset does NOT skip past the "broken" slot — it re-arms
  //     from the same index, because the track is probably fine.
  //   • On network recovery, we opportunistically kick handleTrackEnded
  //     so the DJ hears the queue resume without a 12-second watchdog wait.
  const networkFlakyRef = useRef({ flaky: false, until: 0 });
  // Oct 3 2026 (Poptates repeat RCA) — a handoff declared "silent" can still
  // succeed seconds later on slow venue Wi-Fi. Remember what we TRIED to
  // play so the watchdog can advance the backend pointer once MusicKit is
  // audibly on that track, instead of replaying it when it ends.
  const pendingHandoffRef = useRef(null); // { appleId, nextIndex, at } | null
  const isNetworkFlaky = useCallback(() => {
    const s = networkFlakyRef.current;
    return s.flaky && Date.now() < s.until;
  }, []);
  const nowPlayingIdRef = useRef(null);
  // Tracks which curated/scheduled playlist initiated the current playback.
  // Used by `handleTrackEnded` to auto-load the NEXT slot's playlist when the
  // current one finishes (e.g. morning → afternoon timetable progression).
  const currentPlaylistIdRef = useRef(null);
  // Mirror the current track list into a ref so the MusicKit `nowPlayingItem`
  // listener (defined inside the init useEffect, where React state is stale)
  // can look up the rich track object by Apple Music id when MusicKit
  // auto-advances through the multi-song queue.
  const queueTracksRef = useRef([]);
  // Parallel ref tracking the RESOLVED (post-storefront-remap) Apple Music
  // ids for each queue position. MusicKit's nowPlayingItem.id is the
  // resolved id, which may not match the original track.id in our queue
  // (the original was from the venue's IN catalog, the resolved is from
  // the host's actual storefront). This ref lets us map resolved id →
  // queue index so the player UI stays in lock-step.
  const resolvedIdsRef = useRef([]);

  // ---- Silent Audio Anchor (tab-audibility keep-alive) ----
  // Chrome (esp. on Mac) aggressively throttles tabs that go non-audible
  // for more than a few seconds — and the 2-3s gap between songs (even
  // with the Intelligent Wash) sometimes registers as silent. Once a tab
  // is throttled, our crossfade hand-off `m.play()` calls get deferred
  // until the tab is re-foregrounded, which is exactly the regression
  // hosts hit after ~18 songs of background playback.
  //
  // The fix: loop a ~54dB-attenuated 1-second noise file continuously
  // while `isPlaying === true`. It's inaudible mixed with the music but
  // keeps Chrome's audibility heuristic happy, so the tab is NEVER
  // marked non-audible and is NEVER throttled.
  const silentAnchorRef = useRef(null);
  const startSilentAnchor = useCallback(() => {
    try {
      if (silentAnchorRef.current) return;
      const a = new Audio('/silent_anchor.wav');
      a.loop = true;
      a.volume = 0.05; // already low-amplitude in file; this caps it lower still
      a.preload = 'auto';
      silentAnchorRef.current = a;
      const p = a.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => { silentAnchorRef.current = null; });
      }
    } catch (_) { /* noop */ }
  }, []);
  const stopSilentAnchor = useCallback(() => {
    try {
      const a = silentAnchorRef.current;
      if (!a) return;
      a.pause();
      a.currentTime = 0;
      silentAnchorRef.current = null;
    } catch (_) { /* noop */ }
  }, []);

  useEffect(() => { autoDJRef.current = autoDJ; }, [autoDJ]);
  useEffect(() => { queueTracksRef.current = queue; }, [queue]);

  // ---- MusicKit init ----
  useEffect(() => {
    let cancelled = false;

    // Bridge from the page-level MusicKit error swallower
    // (installed in index.js BEFORE React mounts) — when the filter
    // intercepts an auth-flavoured error/rejection, it dispatches a
    // `spinitup:musickit-auth-error` CustomEvent. We react by
    // unauthorizing MusicKit + surfacing one debounced toast so the DJ
    // knows to reconnect via Settings.
    let lastAuthToastAt = 0;
    const onMusicKitAuthEvent = async () => {
      try { await signOutFromAppleMusic(); } catch { /* noop */ }
      setIsAuthorized(false);
      const now = Date.now();
      if (now - lastAuthToastAt < 4000) return;
      lastAuthToastAt = now;
      toast.error('Apple Music session expired', {
        description: 'Open Settings → Connect Apple Music and sign in again with your Apple Music subscription.',
        duration: 10000,
      });
    };
    window.addEventListener('spinitup:musickit-auth-error', onMusicKitAuthEvent);

    (async () => {
      try {
        const m = await getMusicKit();
        if (cancelled) return;
        musicRef.current = m;
        setPlayerReady(true);
        // If MusicKit says authorized but has no actual user token, flip it
        // back to unauthorized so the UI tells the user to re-connect.
        // Without this the user thinks they're connected, hits Play, and
        // sees "missing MusicUserToken" — exactly the bug we just hit.
        try { await reconcileAuthIfLying(); } catch { /* noop */ }
        const initialAuthed = isAppleMusicAuthorized();
        setIsAuthorized(initialAuthed);
        // Feb 14 2026 — if auto-connect from a persisted token already
        // gave us an authorized singleton on cold-start, unblock
        // explicit content immediately (no `authorizationStatusDidChange`
        // event fires when the state hasn't CHANGED, only when it flips).
        if (initialAuthed) {
          try { unblockExplicitContent(m); } catch { /* noop */ }
        }

        // ==== CRITICAL: force repeatMode = none + autoplay = off ====
        // Feb 13 2026 — added after clients repeatedly reported
        // "songs keep repeating" AFTER we shipped every code-side fix
        // we could think of (skip-past-broken, network-flaky handoff,
        // silent-audio hard recovery, off-by-N click). The remaining
        // suspect was the SDK-level setting we NEVER touched. Verified:
        // `repeatMode` was not set anywhere in the entire codebase —
        // meaning we inherited whatever Apple Music cross-device synced
        // from the DJ's Apple ID. If the DJ ever tapped "repeat one" on
        // their phone (even weeks ago), EVERY venue tablet signed into
        // their account will loop songs, and NO amount of queue-logic
        // gymnastics on our side would override it, because MusicKit's
        // native player is honouring the account-level setting.
        //
        // Similarly, `autoplayEnabled = true` (SDK default) makes
        // MusicKit auto-advance through queue items on its own timeline,
        // fighting our handleTrackEnded logic and creating the exact
        // "one song ends and immediately restarts" symptom on wedged
        // handoffs — because MusicKit's auto-advance kicks in RIGHT
        // after our handoff attempt fails, and it defaults to whatever
        // the last successfully-loaded item was.
        //
        // Both settings are safe to force: we drive advancement
        // ourselves via handleTrackEnded and our own crossfade
        // scheduler; there is no code path in the app that WANTS
        // repeat or SDK-native autoplay.
        try {
          const MK = window.MusicKit;
          const NONE = MK?.PlaybackRepeatMode?.none ?? 0;
          const initialRepeat = m.repeatMode;
          const initialAutoplay = m.autoplayEnabled;
          if (typeof m.repeatMode !== 'undefined' && m.repeatMode !== NONE) {
            console.warn('[MusicContext] MusicKit repeatMode was', m.repeatMode,
                         '- forcing to NONE (0). This was likely the "songs repeat" bug.');
            m.repeatMode = NONE;
            // Fire a distinct telemetry ping so Forensics tells us
            // definitively how many clients had this setting inherited.
            // If this number is > 0 in the fleet, we've confirmed the
            // root cause of the multi-week "songs repeat" reports.
            reportPlaybackStall('sdk_repeat_mode_reset', {
              initial: Number(initialRepeat),
              forced_to: NONE,
              autoplay_was: Boolean(initialAutoplay),
            });
          }
          if (typeof m.autoplayEnabled !== 'undefined' && m.autoplayEnabled) {
            console.info('[MusicContext] Forcing MusicKit autoplayEnabled = false; our handleTrackEnded drives advance.');
            m.autoplayEnabled = false;
          }
        } catch (e) {
          console.warn('[MusicContext] Could not force repeatMode/autoplay:', e?.message);
        }

        // Subscribe to MusicKit lifecycle events. MusicKit v3 uses
        // `addEventListener` with string event names.
        const onAuth = () => {
          const authed = isAppleMusicAuthorized();
          setIsAuthorized(authed);
          // Feb 14 2026 — force-clear the SDK-level explicit-content
          // block every time auth flips to authorized. MusicKit v3
          // lazy-defines `restrictExplicitContent` on the AUTHORIZED
          // singleton (the property doesn't exist on the freshly-
          // configured unauthorized instance, verified via runtime
          // probe). Without this second nudge, Apple's default of
          // `restrictExplicitContent = true` re-blocks every explicit
          // track and our recovery paths spin into the repeat loop.
          if (authed) {
            try { unblockExplicitContent(m); } catch { /* noop */ }
          }
        };
        const onState = (ev) => {
          // ev.state is one of MusicKit.PlaybackStates
          const s = ev?.state ?? m.playbackState;
          // 2 = PLAYING. We treat that as "actually audible".
          setIsPlaying(s === 2);
          if (s === 2) {
            playIntentRef.current = true;
            setShowPlayer(true);
          }
          // 3 = PAUSED. If we ended up paused WITHOUT the user asking
          // (playIntentRef stays `true` because our own pause() clears
          // it), some external force stopped us — audio-focus loss to a
          // ringtone, Chrome tab throttling, a DRM key refresh hiccup.
          // Field report bengaluru.yazu@gmail.com Feb 6 2026: "It
          // automatically pauses."
          //
          // Sep 2026 consolidation: recovery for this used to live HERE too
          // — an inline 0/500/2000ms retry that reported 'paused_mid_song' /
          // 'paused_mid_song_recover_failed' — running IN PARALLEL with the
          // `[isPlaying]` effect below (added Feb 8 2026 as the hardened
          // replacement after field data showed this 3-attempt burst wasn't
          // aggressive enough). Both reacted to the same real pause event:
          // one incident, two uncoordinated retry loops, two different
          // telemetry reasons fragmenting Forensics' counts. The
          // `[isPlaying]` effect is the more complete recovery (indefinite
          // retry, queue-rebuild escalation, eventual toast) and now owns
          // this alone — do not re-add a resume attempt here.

          // 5 = ENDED, 10 = COMPLETED. MusicKit fires these after EACH track
          // ends — but it natively auto-advances through the multi-song
          // queue we loaded via setQueue({songs: [...]}). We must NOT call
          // /api/queue/next + setQueue here for mid-playlist track ends —
          // that triggers MusicKit's "play() without a previous stop()"
          // error and breaks the natural advance. Only fall back to our
          // handleTrackEnded (which loads the next scheduled slot's
          // playlist) when MusicKit's queue is genuinely EXHAUSTED.
          if ((s === 5 || s === 10) && autoDJRef.current && !queueLockRef.current) {
            const q = m.queue;
            const atEnd = !q ||
              (typeof q.position === 'number' && typeof q.length === 'number' && q.position >= q.length - 1);
            // Feb 8 2026 — track WHEN the crossfade didn't fire before
            // this track ended. Field report from Yazu Bangalore: "one
            // song ends and next one starts — no DJ mix". That's
            // exactly this path: we're falling into MusicKit's native
            // advance (hard cut, no crossfade) because the T-8s trigger
            // was throttled or missed. Report so Forensics tells us how
            // often, and on which tracks.
            const endedId = nowPlayingIdRef.current;
            if (endedId && crossfadeTriggeredForIdRef.current !== endedId) {
              reportPlaybackStall('crossfade_missed_hardcut', {
                track_id: endedId,
                state: s,
                is_hidden: typeof document !== 'undefined' ? document.hidden : null,
                queue_locked: !!queueLockRef.current,
                fading: !!fadeIntervalRef.current,
              });
            }
            if (!atEnd) {
              // MusicKit moves the queue pointer forward on ENDED but does
              // NOT always auto-start the next item — on Chrome/Android in
              // particular it leaves the new track paused at 0:00. We
              // explicitly call play() to keep the queue going.
              //
              // Retry once after 500ms if the first play() promise rejects.
              // On Android screen-off (Samsung Fold 6 field report Jul 2026),
              // audio focus can be briefly released across the ENDED event
              // and the very first play() attempt is rejected; the OS
              // usually grants focus back within ~300-500ms so a single
              // retry recovers background playback reliably.
              //
              // Feb 2026 hardening (production stall RCA): after play(),
              // VERIFY playbackState becomes 2 (PLAYING). If it doesn't
              // within ~1.5s, MusicKit's native advance silently stalled
              // — force a rebuild via handleTrackEnded instead of
              // fire-and-forget /queue/next.
              const tryPlay = async (attempt) => {
                try {
                  const p = m.play?.();
                  if (p && typeof p.then === 'function') await p;
                } catch (err) {
                  console.warn('[Playback] m.play() rejected on ENDED (attempt=', attempt, ') —', err?.name || err?.message);
                  if (attempt === 1) {
                    setTimeout(() => { tryPlay(2); }, 500);
                  }
                }
              };
              (async () => {
                await tryPlay(1);
                // Verify audio actually started. `playbackState === 2` is
                // MusicKit's PLAYING state. If we can't get there in 1.5s,
                // the native advance failed silently — invoke the
                // rebuild-capable handleTrackEnded before we sync the
                // backend pointer.
                const started = await new Promise((resolve) => {
                  let elapsed = 0;
                  const iv = setInterval(() => {
                    if (m.playbackState === 2) { clearInterval(iv); resolve(true); return; }
                    elapsed += 100;
                    if (elapsed >= 1500) { clearInterval(iv); resolve(false); }
                  }, 100);
                });
                if (!started) {
                  console.warn('[Playback] ENDED native-advance stalled — playbackState never reached 2. Forcing rebuild via handleTrackEnded.');
                  reportPlaybackStall('ended_native_advance_stalled', {
                    now_playing_id: nowPlayingIdRef.current,
                  });
                  // Skip our own /queue/next below; handleTrackEnded owns
                  // the rebuild path AND the (post-success) backend advance.
                  const now = Date.now();
                  if (now - lastEndedAdvanceAtRef.current > 3000) {
                    lastEndedAdvanceAtRef.current = now;
                    handleTrackEndedRef.current?.();
                  }
                  return;
                }
                // Playback verified — safe to sync backend pointer.
                // (Guard against the crossfade path having already advanced
                // it within the last 8s to avoid TV "1 full song ahead" bug.)
                const sinceCrossfadeAdvance = Date.now() - lastCrossfadeAdvanceAtRef.current;
                if (sinceCrossfadeAdvance > 8000) {
                  axios.post(`${API}/queue/next`).catch(() => {});
                } else {
                  console.info('[QueueSync] ENDED-advance suppressed —', sinceCrossfadeAdvance, 'ms since crossfade advanced');
                }
              })();
              return;
            }
            const now = Date.now();
            if (now - lastEndedAdvanceAtRef.current > 3000) {
              lastEndedAdvanceAtRef.current = now;
              handleTrackEndedRef.current?.();
            }
          }
        };
        const onNowPlaying = () => {
          const np = m.nowPlayingItem;
          if (!np) {
            nowPlayingIdRef.current = null;
            return;
          }
          // Feb 13 2026 — belt-and-suspenders repeatMode snap.
          // Every track transition = one more chance for MusicKit to
          // silently re-inherit the account-level repeatMode setting.
          // Snapping to NONE here catches any case where a background
          // Apple Music sync flipped it back on the DJ's account
          // mid-shift, or where a queue rebuild path we forgot silently
          // re-read the account setting. Cheap (one assignment) and
          // idempotent — if it's already 0 the assignment is a no-op.
          try {
            const NONE = window.MusicKit?.PlaybackRepeatMode?.none ?? 0;
            if (typeof m.repeatMode !== 'undefined' && m.repeatMode !== NONE) {
              console.warn('[MusicContext] repeatMode drifted to', m.repeatMode, 'during track change — snapping back to NONE');
              reportPlaybackStall('sdk_repeat_mode_drift', {
                drifted_to: Number(m.repeatMode),
                snapped_to: NONE,
              });
              m.repeatMode = NONE;
            }
          } catch { /* noop */ }
          // np.id is the catalog song id when playing a catalog track.
          const id = String(np.id || np.container?.id || '');
          nowPlayingIdRef.current = id;
          // Reset crossfade + wash + stall + prewarm dedupe for the new track.
          crossfadeTriggeredForIdRef.current = null;
          washTriggeredForIdRef.current = null;
          stallCrossfadeFiredForIdRef.current = null;
          prewarmSucceededForIdRef.current = null;
          stalledTicksRef.current = 0;
          lastPlayheadCurRef.current = 0;

          // Reflect MusicKit's auto-advance into React state so the player
          // bar + deck UIs display the actually-playing track, not whatever
          // we last optimistically set. The MusicKit id may differ from the
          // queue tracks' original `id` (storefront remap), so we ALSO
          // consult `resolvedIdsRef` which is the per-position resolved
          // id parallel array filled by `playTracks`.
          const list = queueTracksRef.current || [];
          const resolved = resolvedIdsRef.current || [];
          let idx = list.findIndex((t) => String(t?.id) === id || String(t?.apple_id) === id);
          if (idx < 0) idx = resolved.findIndex((rid) => String(rid) === id);
          let track = idx >= 0 ? list[idx] : null;
          if (!track) {
            const attrs = np.attributes || {};
            track = {
              id,
              apple_id: id,
              name: np.title || attrs.name || 'Now playing',
              artists: attrs.artistName ? [attrs.artistName] : [],
              album: attrs.albumName || '',
              album_image: (attrs.artwork?.url || '').replace('{w}', '300').replace('{h}', '300').replace('{f}', 'jpg'),
              duration_ms: Number(attrs.durationInMillis || 0),
            };
          }
          setCurrentTrack(track);
          if (idx >= 0) {
            setQueueIndex(idx);
            setNextUp(list[idx + 1] || null);
          }
          // Mirror to whichever deck is currently active.
          if (activeSlotRef.current === 'A') setSlotATrack(track);
          else setSlotBTrack(track);
        };

        // Feb 13 2026 — track every MusicKit listener we attach so the
        // cleanup can remove them ALL. Previously cleanup only removed
        // the `window` auth listener; when `musicKitEpoch` bumped
        // (emergency hard recover), the init effect re-attached NEW
        // handlers on the same singleton without removing the old ones.
        // Each emergency recovery doubled the handler count → every
        // subsequent advance fired twice → doubled /queue/next calls
        // and instability compounded over a shift.
        const mkListeners = [];
        const trackedAdd = (name, fn) => {
          m.addEventListener(name, fn);
          mkListeners.push({ name, fn });
        };
        mkListenersRef.current = mkListeners;
        trackedAdd('authorizationStatusDidChange', onAuth);
        trackedAdd('playbackStateDidChange', onState);
        trackedAdd('nowPlayingItemDidChange', onNowPlaying);

        // === Resilience event hooks (Feb 6 2026) =====================
        // Field reports of "auto-pauses" with ZERO telemetry recorded
        // meant we were completely blind to MusicKit-side errors —
        // the SDK emits a whole family of `media*` and buffer events
        // that we were ignoring. Wire all of them, log to forensics,
        // and attempt an automatic recovery on the ones that indicate
        // playback broke. Every handler is defensive because MusicKit
        // JS v3 has silently renamed events between minor versions.
        const attemptRecover = (why, meta = {}) => {
          // Skip entirely if the user is deliberately paused — MusicKit
          // fires media-state events (`interrupted`, `stalled`, etc.)
          // during a legitimate pause and we don't want those cluttering
          // Forensics as false-positive failures.
          if (!playIntentRef.current) return;
          try {
            reportPlaybackStall(`musickit_${why}`, {
              now_playing_id: nowPlayingIdRef.current,
              playback_state: m.playbackState,
              current_time: (() => { try { return m.currentPlaybackTime; } catch { return null; } })(),
              duration: (() => { try { return m.currentPlaybackDuration; } catch { return null; } })(),
              is_hidden: typeof document !== 'undefined' ? document.hidden : null,
              ...meta,
            });
          } catch (_) { /* noop */ }
          // Debounce — recoveries are cheap but calling handleTrackEnded
          // 5x in 500ms from a burst of error events is not.
          const now = Date.now();
          if (now - (attemptRecover._lastAt || 0) < 3000) return;
          attemptRecover._lastAt = now;
          // Retry play in place first (cheapest); handleTrackEnded is
          // more surgical if that fails.
          Promise.resolve().then(async () => {
            try { await m.play?.(); } catch (_) { /* noop */ }
            setTimeout(() => {
              try {
                if (m.playbackState !== 2 && playIntentRef.current) {
                  handleTrackEndedRef.current?.();
                }
              } catch (_) { /* noop */ }
            }, 2500);
          });
        };
        const onMediaError = (ev) => {
          const reason = ev?.type || ev?.error?.message || ev?.errorCode || 'unknown';
          // Feb 12 2026 — richer diagnostic capture. Field reports of
          // "current song loops when it should transition" (Yazu:
          // Watermelon → thank u next; Billie Jean → Tum Mile). The
          // hypothesis is Apple's SDK is erroring on setQueue-with-a-
          // single-song for certain tracks but the error was previously
          // logged with only a truncated reason. Capture the FULL error
          // shape so we can pattern-match across venues.
          console.warn('[MusicKit] media error', reason, ev);
          reportPlaybackStall('musickit_media_error', {
            reason: String(reason).slice(0, 200),
            error_code: ev?.errorCode ?? ev?.error?.errorCode ?? null,
            error_name: ev?.error?.name ?? null,
            now_playing_id: nowPlayingIdRef.current,
            playback_state: musicRef.current?.playbackState,
          });
          attemptRecover('media_error', { reason: String(reason).slice(0, 120) });
        };
        const onMediaCanNotBeLoaded = (ev) => {
          console.warn('[MusicKit] mediaCanNotBeLoaded', ev);
          attemptRecover('media_cannot_be_loaded', {
            reason: String(ev?.error?.message || ev?.errorCode || 'load_failed').slice(0, 120),
          });
        };
        const onMediaFailedToPlay = (ev) => {
          console.warn('[MusicKit] mediaFailedToPlay', ev);
          attemptRecover('media_failed_to_play', {
            reason: String(ev?.error?.message || ev?.errorCode || 'play_failed').slice(0, 120),
          });
        };
        const onMediaItemState = (ev) => {
          // MediaItemState: 0=none 1=loading 2=playable 3=playing 4=paused
          // 5=interrupted 6=seeking 7=stalled 8=waiting 9=ended
          const s = ev?.state ?? ev?.mediaItemState;
          if (s === 7 || s === 5) {
            // Skip when the user is deliberately paused — `interrupted`
            // legitimately fires during a manual pause and we don't
            // want that in the failure log.
            if (!playIntentRef.current) return;
            // stalled or interrupted — worth a report but no immediate
            // recover (Apple may auto-resume; we let watchdog handle
            // it if not).
            reportPlaybackStall('media_item_stalled', {
              media_state: s, now_playing_id: nowPlayingIdRef.current,
            });
          }
        };
        // Not all MusicKit builds expose these; wrap each in try/catch.
        const safeOn = (name, fn) => {
          try {
            m.addEventListener(name, fn);
            mkListeners.push({ name, fn });
          } catch (_) { /* noop */ }
        };
        safeOn('mediaError',            onMediaError);
        safeOn('mediaPlaybackError',    onMediaError);
        safeOn('mediaCanNotBeLoaded',   onMediaCanNotBeLoaded);
        safeOn('mediaFailedToPlay',     onMediaFailedToPlay);
        safeOn('mediaItemStateDidChange', onMediaItemState);
      } catch (err) {
        console.error('[MusicContext] MusicKit init failed:', err);
        setPlayerReady(false);
      }
    })();
    return () => {
      cancelled = true;
      window.removeEventListener('spinitup:musickit-auth-error', onMusicKitAuthEvent);
      // Feb 13 2026 — detach ALL MusicKit listeners on cleanup so a
      // re-run (from a musicKitEpoch bump) doesn't leave stale handlers
      // attached to the singleton. Without this, each emergency recover
      // doubled the number of `nowPlayingItemDidChange` /
      // `playbackStateDidChange` handlers and cascaded into more races.
      try {
        const mForCleanup = musicRef.current;
        const listeners = mkListenersRef.current || [];
        if (mForCleanup && listeners.length) {
          listeners.forEach(({ name, fn }) => {
            try { mForCleanup.removeEventListener(name, fn); } catch { /* noop */ }
          });
        }
        mkListenersRef.current = [];
      } catch { /* noop */ }
    };
  }, [musicKitEpoch]);

  // ---- Helpers ----
  const refreshQueue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/queue/current`);
      setQueue(res.data.tracks || []);
      setQueueIndex(res.data.current_index || 0);
      setNextUp(res.data.next_track || null);
      return res.data;
    } catch { return null; }
  }, []);

  const killFade = useCallback(() => {
    if (fadeIntervalRef.current) {
      fadeIntervalRef.current.clear();
      fadeIntervalRef.current = null;
    }
    setCrossfadeActive(false);
    // Restore volume
    const m = musicRef.current;
    if (m) { try { m.volume = 1.0; } catch (e) { /* noop */ } }
  }, []);

  // Resolve a track to a CATALOG SONG ID that is valid in the user's Apple
  // Music storefront. This is the key fix for non-IN accounts: even when a
  // track already carries a numeric Apple ID, that ID may be from the IN
  // storefront (where it was originally indexed) and won't exist in the
  // user's actual storefront — `m.setQueue` then fails silently with
  // AUTHORIZATION_ERROR. We always re-search by name+artist in the user's
  // own storefront so the returned ID is guaranteed playable for them.
  const resolveAppleId = useCallback(async (track) => {
    if (!track) return null;
    // Feb 12 2026 fix — the field-report "click track 3, plays track 5"
    // bug traced to this function firing 51 pointless Apple search calls
    // per playlist load (backend imports store `apple_id` not `id`, so
    // the old caller's `looksLikeAppleId(t.id)` check missed them). Any
    // transient search miss dropped a track from `playable` and shifted
    // the start index. Short-circuit for tracks that ALREADY have a
    // usable Apple ID — the whole point of resolveAppleId is to *find*
    // one, not to re-verify one that's right there.
    if (looksLikeAppleId(track.id)) return track.id;
    if (looksLikeAppleId(track.apple_id)) return track.apple_id;
    const m = musicRef.current;
    const userStorefront = (m?.storefrontCountryCode || m?.storefrontId || '').toLowerCase();
    const artists = Array.isArray(track.artists) ? track.artists.join(' ') : (track.artists || '');
    const q = `${track.name || ''} ${artists}`.trim();
    if (!q) return null;
    try {
      const params = { q, limit: 1 };
      if (userStorefront) params.storefront = userStorefront;
      const r = await axios.get(`${API}/music/search`, { params });
      const first = (r.data?.tracks || [])[0];
      if (first?.id) return first.id;
    } catch (e) {
      console.warn('[MusicContext] resolveAppleId failed:', e?.message);
    }
    // If we DID know the user's storefront and it still returned no match,
    // the track isn't available on their subscription — return null so
    // playTracks filters it out.
    if (userStorefront) return null;
    return null;
  }, []);

  // Set the MusicKit queue to a list of Apple Music catalog song IDs.
  // We deliberately mirror the parameter shape proven to work in the
  // AppleMusicConnectionCard's "Test Background Play" button: `song` for a
  // single id, `songs` for an array. The `startWith` key is NOT a documented
  // MusicKit JS v3 parameter — passing it caused silent setQueue failures.
  // To start at a non-zero index we use `changeToMediaAtIndex` afterwards.
  const setAppleQueue = useCallback(async (ids, startIndex = 0, opts = {}) => {
    const m = musicRef.current;
    if (!m || !ids.length) return { playedIds: [] };
    // Optional `tracksById` map lets us recover from Apple's "could not be
    // resolved" error by re-searching the LIVE user storefront for each
    // failed ID's track metadata. Without this map we can only prune (drop
    // the failed IDs). The playTracks caller populates it; single-track
    // callers (nextTrack, injectNext, handleTrackEnded, crossfade rebuild)
    // fall back to prune-only.
    const tracksById = opts.tracksById || {};
    // Crossfade rebuild path opts into `noPause: true` — MusicKit's
    // `setQueue` internally stops the current track, so calling pause first
    // costs us ~150-300ms of hard silence we don't need. The subsequent
    // `play()` right after setQueue does NOT throw the "play() without
    // stop/pause" error in MusicKit v3.x when setQueue has just replaced
    // the queue (verified against MusicKit v3.2317 on Chrome + Safari).
    const noPause = !!opts.noPause;
    const attempt = async (currentIds) => {
      // MusicKit JS contract: calling `setQueue` + `play()` while a track is
      // ALREADY playing throws "The play() method was called without a
      // previous stop() or pause() call." Always pause first UNLESS caller
      // opts out (crossfade wants a direct swap for zero-silence).
      if (!noPause) {
        try { if (m.playbackState === 2) await m.pause(); }
        catch (_) { /* already paused / idle — fine */ }
      }
      if (currentIds.length === 1) {
        await m.setQueue({ song: currentIds[0] });
      } else {
        await m.setQueue({ songs: currentIds });
        const idx = Math.max(0, Math.min(startIndex, currentIds.length - 1));
        if (idx > 0 && typeof m.changeToMediaAtIndex === 'function') {
          try { await m.changeToMediaAtIndex(idx); } catch (e) { /* MusicKit will still start at 0 */ }
        }
      }
      // Feb 13 2026 — MusicKit's `setQueue` occasionally re-reads the
      // account-level repeatMode from Apple Music (behavior varies by
      // SDK version + storefront). Re-force it to NONE after every
      // queue swap so the "songs keep repeating" bug can't reappear
      // between page load and the first track transition.
      try {
        const NONE = window.MusicKit?.PlaybackRepeatMode?.none ?? 0;
        if (typeof m.repeatMode !== 'undefined' && m.repeatMode !== NONE) {
          m.repeatMode = NONE;
        }
        if (typeof m.autoplayEnabled !== 'undefined' && m.autoplayEnabled) {
          m.autoplayEnabled = false;
        }
      } catch { /* noop */ }
    };
    try {
      await attempt(ids);
      return { playedIds: ids };
    } catch (err) {
      const msg = err?.message || err?.toString?.() || '';
      const failedIds = parseUnresolvableIds(msg);
      if (!failedIds.length) {
        console.error('[MusicContext] setQueue failed:', err, { ids, startIndex });
        throw err;
      }
      // ===== Self-healing recovery for "could not be resolved" =====
      // Apple's MusicKit is telling us these IDs don't exist in the CURRENT
      // Apple Music user's storefront. Two paths to try, in order:
      //   1. If we have the original track metadata (via tracksById), re-run
      //      resolveAppleId against the LIVE storefront to get a fresh id
      //      for THIS user's account.
      //   2. Otherwise, drop the failed IDs and retry with the remainder.
      console.warn('[MusicContext] setQueue reported unresolvable IDs:',
        failedIds, '| liveStorefront=', m.storefrontId, '— attempting recovery');
      const failedSet = new Set(failedIds.map(String));
      const recovered = [];
      for (const id of ids) {
        const sid = String(id);
        if (!failedSet.has(sid)) { recovered.push(id); continue; }
        const trk = tracksById[sid];
        if (!trk) {
          console.info('[MusicContext] pruning', sid, '(no track metadata for re-resolve)');
          continue;
        }
        try {
          const newId = await resolveAppleId(trk);
          if (newId && String(newId) !== sid) {
            console.info('[MusicContext] re-resolved', sid, '→', newId, 'for', trk?.name);
            recovered.push(String(newId));
          } else {
            console.info('[MusicContext] pruning', sid, '(re-resolve returned nothing new)');
          }
        } catch (e) {
          console.warn('[MusicContext] re-resolve failed for', sid, ':', e?.message);
        }
      }
      if (!recovered.length) {
        const enriched = new Error(
          `Playback failed — none of the requested tracks are available in your Apple Music (${(m.storefrontId || '').toUpperCase() || 'account'}) storefront.`,
        );
        enriched.code = 'ALL_UNRESOLVABLE';
        enriched.original = err;
        throw enriched;
      }
      try {
        await attempt(recovered);
      } catch (retryErr) {
        console.error('[MusicContext] setQueue retry also failed:', retryErr);
        throw retryErr;
      }
      const dropped = ids.length - recovered.length;
      if (dropped > 0) {
        toast.warning(`${dropped} track${dropped === 1 ? '' : 's'} skipped`, {
          description: `Not available in your Apple Music (${(m.storefrontId || '').toUpperCase() || 'current'}) storefront.`,
          duration: 5000,
        });
      }
      return { playedIds: recovered };
    }
  }, [resolveAppleId]);

  // ---- Public API ----

  const playTracks = useCallback(async (trackObjects, startIndex = 0, opts = {}) => {
    if (!trackObjects.length) return;
    // Item 5 master gate — see `play()` above for the rationale.
    if (deviceEnabled && !isMasterRef.current) {
      toast.info('This tab is not the audio Master', {
        description: 'Click "Take Over" in the booth to control playback from here.',
      });
      return;
    }
    const m = musicRef.current;
    if (!m) { toast.error('Music engine not ready — wait a moment and retry'); return; }

    // Apple Music REQUIRES the user be authorized before playing any catalog
    // content. We use `isAppleMusicAuthorized()` (which verifies a real
    // MusicUserToken exists, not just MusicKit's lying `isAuthorized` flag).
    //
    // Also pre-flight `reconcileAuthIfLying` on every play attempt — the
    // token can be silently wiped mid-session by Safari ITP / cookie clear /
    // Apple's own storefront-invalidation flow, and MusicKit's cached
    // `isAuthorized` flag keeps returning true until the next call to
    // `_prepareMediaAPIItem` throws "missing MusicUserToken" from deep
    // inside MusicKit's async chain (which then bubbles as an UNCAUGHT
    // rejection — CRA overlay pops, host thinks the app crashed).
    try { await reconcileAuthIfLying(); } catch { /* noop */ }
    if (!isAppleMusicAuthorized()) {
      setIsAuthorized(false);
      toast.error('Connect Apple Music first', {
        description: 'Open Settings → "Connect Apple Music" and sign in with your Apple Music subscription.',
        duration: 8000,
      });
      return;
    }

    killFade();
    queueLockRef.current = false;
    playIntentRef.current = true;
    // Remember which curated playlist (if any) is driving this session so
    // `handleTrackEnded` can hop to the NEXT slot's playlist when it finishes.
    currentPlaylistIdRef.current = opts.playlistId || null;
    startKeepAlive();
    try { armSilentAudio(); } catch (e) { /* silent anchor is best-effort */ }

    // Optimistic UI — Feb 13 2026 revision.
    // Field report (Punjabi Hits playlist): clicking song 6 while song 1
    // was playing painted "God's Favorite" as playing but audio was Low
    // Fade. Cause: setCurrentTrack(clickedTrack) and setQueueIndex(idx)
    // fired here BEFORE the setAppleQueue swap; when the swap silently
    // no-op'd (MusicKit was mid-crossfade), currentTrack was frozen at
    // the clicked track while MusicKit kept playing something else.
    //
    // Fix: only paint the QUEUE LIST + showPlayer optimistically here.
    // `currentTrack`, `queueIndex`, and the crossfade slot tracks are
    // deferred until `setAppleQueue` succeeds and the `nowPlayingItemDidChange`
    // listener confirms MusicKit actually landed on the target — that
    // way the UI can never LIE about what's playing. Worst case is a
    // 500-1500ms delay before the highlighted row updates; acceptable.
    setQueue(trackObjects);
    setShowPlayer(true);

    // ===== Storefront-aware Apple ID resolution =====
    // Tracks were originally indexed via our IN-storefront catalog search,
    // so their `id` is only valid in IN. If the host's Apple Music account
    // is in a different storefront, we MUST re-search Apple's catalog in
    // their storefront before play — otherwise setQueue throws
    // AUTHORIZATION_ERROR. We resolve in parallel (Promise.all) so iOS
    // Safari's user-gesture window stays warm.
    //
    // Feb 12 2026 fix (Yazu Bangalore field report: "click track 3, plays
    // track 5"): Backend playlist tracks (backend/services/apple_music.py
    // import path) are stored with the field `apple_id`, not `id`. The
    // previous `looksLikeAppleId(t.id)` check missed these — so every
    // playlist load fired 51 unnecessary Apple search calls, and any
    // transient search failure dropped that track from `playable`. The
    // caller's `startIndex` then pointed at the wrong slot in the filtered
    // array (off-by-N = number of dropped songs before startIndex).
    //
    // Prefer `apple_id` when present. Only fall back to name+artist search
    // when we have no valid Apple ID at all.
    const bestAppleId = (t) => {
      if (looksLikeAppleId(t?.id)) return t.id;
      if (looksLikeAppleId(t?.apple_id)) return t.apple_id;
      return null;
    };
    const userStorefront = (m.storefrontCountryCode || m.storefrontId || 'in').toLowerCase();
    const needsRemap = userStorefront !== 'in';
    console.info('[MusicContext] playTracks | userStorefront=', userStorefront,
      '| storefrontId=', m.storefrontId, '| countryCode=', m.storefrontCountryCode,
      '| tracks=', trackObjects.length, '| needsRemap=', needsRemap);
    let resolveTuples; // [{ track, id }] keeping positional correspondence
    if (needsRemap) {
      const settled = await Promise.all(
        trackObjects.map((t) => resolveAppleId(t).catch(() => null)),
      );
      resolveTuples = trackObjects.map((t, i) => ({ track: t, id: settled[i] }));
    } else {
      const allAlreadyApple = trackObjects.every((t) => bestAppleId(t));
      if (allAlreadyApple) {
        resolveTuples = trackObjects.map((t) => ({ track: t, id: bestAppleId(t) }));
      } else {
        const settled = await Promise.all(
          trackObjects.map((t) => {
            const known = bestAppleId(t);
            return known ? known : resolveAppleId(t).catch(() => null);
          }),
        );
        resolveTuples = trackObjects.map((t, i) => ({ track: t, id: settled[i] }));
      }
    }

    // Keep only tracks that successfully resolved to a playable Apple id —
    // and keep the queue + resolvedIdsRef perfectly aligned so the
    // nowPlayingItemDidChange listener can map resolved id → queue index.
    const playable = resolveTuples.filter((x) => x.id);
    const resolvedIds = playable.map((x) => x.id);
    const playableTracks = playable.map((x) => ({ ...x.track, apple_id: x.id }));
    resolvedIdsRef.current = resolvedIds;

    if (!resolvedIds.length) {
      toast.error('No playable tracks found on Apple Music', {
        description: `Tracks may not be available in your "${userStorefront.toUpperCase()}" Apple Music storefront.`,
      });
      return;
    }

    // If SOME tracks failed to resolve (storefront mismatch), let the
    // user know count. Silently drops from the queue but the toast tells
    // them why the count shrank — otherwise they see "27 tracks queued"
    // when they added 30 and wonder what happened.
    const skipped = trackObjects.length - playable.length;
    if (skipped > 0) {
      const sfLabel = userStorefront ? `"${userStorefront.toUpperCase()}"` : 'your';
      toast.warning(`${skipped} track${skipped === 1 ? '' : 's'} skipped`, {
        description: `Not available in ${sfLabel} Apple Music storefront. ${playable.length} playable.`,
        duration: 5000,
      });
    }

    // Re-sync UI state to the playable subset so optimistic positions
    // match what MusicKit actually loads.
    setQueue(playableTracks);

    // Feb 12 2026 fix — off-by-N: `startIndex` was measured against the
    // ORIGINAL trackObjects array, but we're about to play from the
    // FILTERED `playable` array. If any tracks before startIndex were
    // dropped, indexes shift. The old code did `min(startIndex, len-1)`
    // which "worked" as long as nothing was dropped — and silently played
    // the wrong track otherwise. Compute the correct position by finding
    // the ORIGINAL target track object in the playable list. Fall back
    // to next-available-≥-startIndex if the target itself was dropped.
    const targetTrack = trackObjects[startIndex];
    let effectiveStart = 0;
    if (targetTrack) {
      const pos = playable.findIndex((x) => x.track === targetTrack);
      if (pos >= 0) {
        effectiveStart = pos;
      } else {
        // Target was filtered out — find the next surviving track after it.
        for (let i = startIndex + 1; i < trackObjects.length; i++) {
          const p = playable.findIndex((x) => x.track === trackObjects[i]);
          if (p >= 0) { effectiveStart = p; break; }
        }
      }
    }
    // Feb 14 2026 fix — DO NOT eagerly setQueueIndex / setNextUp here.
    //
    // Field report (Cafe Strokes screenshot, 2000s Hype playlist): user
    // clicked track 5 (Dynamite), UI painted "5 / 49 Dynamite" as playing,
    // but Apple MusicKit remained on track 2 (Now You're Gone) in a
    // repeat loop. Cause: `setQueueIndex(effectiveStart)` fired
    // synchronously here, painting the highlight on the clicked row
    // BEFORE `changeToMediaAtIndex` had a chance to succeed or fail. When
    // Apple silently no-op'd the jump (mid-crossfade quirk), the UI was
    // frozen on a lie.
    //
    // Correct source of truth = MusicKit's own `nowPlayingItemDidChange`
    // event (see onNowPlaying handler above, ~line 900). It calls
    // setQueueIndex + setNextUp with the ACTUAL landed track. Worst case:
    // 500-1500ms delay before the highlighted row updates. Acceptable.

    // Build id → track map so setAppleQueue can self-heal on
    // "could not be resolved" errors by re-searching the LIVE storefront.
    const tracksById = {};
    playable.forEach((x) => { if (x.id) tracksById[String(x.id)] = x.track; });

    // === Feb 14 2026 revision: same-queue jump fast path (hardened) ===
    //
    // Original fast-path (Feb 13) called `changeToMediaAtIndex` directly
    // on an actively-playing MusicKit instance. In the field (Cafe
    // Strokes, 2000s Hype) this collided with an in-flight crossfade /
    // prewarm and Apple SILENTLY NO-OP'D the call — the promise resolved
    // but the queue index never moved. Result: user saw a stuck loop on
    // the previous track no matter which row they clicked.
    //
    // Three defenses added below:
    //   1. Wait for any in-flight recovery (handleTrackEnded / hardReset /
    //      emergencyHardRecover) to finish before touching MusicKit, and
    //      hold the recovery mutex during the jump so those paths cannot
    //      pre-empt us mid-swap.
    //   2. `await m.stop()` BEFORE `changeToMediaAtIndex`. This forces
    //      MusicKit out of its crossfade/prewarm state; the subsequent
    //      changeToMediaAtIndex call then actually re-seeks.
    //   3. Verify success: after `play()`, poll `m.nowPlayingItem?.id`
    //      for up to 1.5s. If it doesn't match the target resolved ID,
    //      treat the fast path as failed and fall through to the full
    //      setAppleQueue rebuild — never leave the click unrecoverable.
    const prevIds = resolvedIdsRef.current || [];
    const sameQueue = prevIds.length === resolvedIds.length
      && prevIds.length > 0
      && prevIds.every((id, i) => id === resolvedIds[i]);

    if (sameQueue && typeof m.changeToMediaAtIndex === 'function') {
      // Wait up to 1500ms for any in-flight recovery path to complete.
      const waitStart = Date.now();
      while (recoveryInFlightRef.current && Date.now() - waitStart < 1500) {
        await new Promise((r) => setTimeout(r, 50));
      }
      const acquired = acquireRecovery('playTracks:sameQueueJump');
      let fastPathVerified = false;
      try {
        console.info('[playTracks] same-queue jump fast path → stop() + changeToMediaAtIndex(', effectiveStart, ')');
        // Defense #2 — break out of crossfade/prewarm before jumping.
        try { await m.stop(); } catch (e) { /* stop is best-effort */ }
        await m.changeToMediaAtIndex(effectiveStart);
        // Belt-and-suspenders: enforce repeatMode+autoplay per Feb 13 fix
        try {
          const NONE = window.MusicKit?.PlaybackRepeatMode?.none ?? 0;
          if (typeof m.repeatMode !== 'undefined' && m.repeatMode !== NONE) m.repeatMode = NONE;
          if (typeof m.autoplayEnabled !== 'undefined' && m.autoplayEnabled) m.autoplayEnabled = false;
        } catch { /* noop */ }
        await m.play();

        // Defense #3 — verify Apple actually landed on the target ID.
        const targetId = String(resolvedIds[effectiveStart] || '');
        const verifyStart = Date.now();
        while (Date.now() - verifyStart < 1500) {
          const nowId = String(m.nowPlayingItem?.id || m.nowPlayingItem?.container?.id || '');
          if (nowId && nowId === targetId) { fastPathVerified = true; break; }
          await new Promise((r) => setTimeout(r, 100));
        }
        if (!fastPathVerified) {
          console.warn('[playTracks] fast-path DID NOT VERIFY (target=', targetId, 'nowPlaying=', m.nowPlayingItem?.id, ') — falling through to full rebuild');
          throw new Error('fast-path verification failed');
        }
        // Backend sync — use effectiveStart + playableTracks (Feb 13 fix)
        try {
          await axios.post(`${API}/queue/load`, {
            tracks: playableTracks,
            start_index: effectiveStart,
            playlist_id: opts.playlistId || null,
            playlist_type: opts.playlistType || null,
          });
        } catch (e) {
          console.warn('[playTracks] backend sync (fast path) failed:', e?.message);
        }
        return;
      } catch (fastErr) {
        console.warn('[playTracks] fast-path failed, falling back to full rebuild:', fastErr?.message);
        // Fall through to the normal setAppleQueue path below.
      } finally {
        // Always release the mutex when fast-path exits (success OR failure),
        // otherwise the fallback setAppleQueue path — and every future
        // recovery — would be blocked.
        if (acquired) releaseRecovery();
      }
    }

    try {
      const { playedIds } = await setAppleQueue(resolvedIds, effectiveStart, { tracksById });
      // If setAppleQueue's self-healer had to prune or re-resolve, sync the
      // parallel ref so nowPlayingItemDidChange can still map resolved id →
      // queue index correctly.
      if (playedIds && playedIds.length !== resolvedIds.length) {
        resolvedIdsRef.current = playedIds;
      } else if (playedIds) {
        resolvedIdsRef.current = playedIds;
      }
      await m.play();
    } catch (err) {
      console.error('[MusicContext] playTracks play failed:', err, {
        isAuthorized: m.isAuthorized,
        storefrontId: m.storefrontId,
        firstResolvedId: resolvedIds[0],
        errCode: err?.code || err?.errorCode,
        errName: err?.name,
      });
      const rawMsg = err?.message || err?.toString?.() || '';
      // Diagnostic toast — translate MusicKit's most common failure modes
      // into actionable user guidance instead of "Playback failed".
      if (/NotAllowed|user.?gesture|gesture/i.test(rawMsg) || err?.name === 'NotAllowedError') {
        toast.error('Tap Play once more', {
          description: 'iOS blocked the first audio start. Tap Play again on the playlist.',
          duration: 8000,
        });
      } else if (/subscription|SUBSCRIPTION|not.*subscrib/i.test(rawMsg)) {
        toast.error('Active Apple Music subscription required', {
          description: 'This Apple ID does not have an active Apple Music plan. Subscribe at music.apple.com and retry.',
          duration: 10000,
        });
      } else if (/authoriz|AUTHORIZATION|token/i.test(rawMsg)) {
        toast.error('Apple Music session expired', {
          description: 'Go to Settings → Disconnect & reconnect Apple Music to refresh your session.',
          duration: 10000,
        });
      } else if (err?.code === 'ALL_UNRESOLVABLE') {
        // Every track we asked Apple to load was rejected as "not resolvable"
        // in this user's storefront — even after our re-resolve retry. The
        // playlist is almost certainly full of region-locked tracks that
        // this Apple Music subscription can't stream.
        toast.error('Tracks unavailable in your Apple Music region', {
          description: rawMsg || 'None of these tracks are stocked in your storefront. Try a different playlist, or connect an Apple Music account in the region where these tracks are published.',
          duration: 12000,
        });
      } else if (/storefront|region|not available|NOT_FOUND|404|could not be resolved/i.test(rawMsg)) {
        toast.error('Track not available in your region', {
          description: 'Your Apple Music storefront does not stock this song. Try another track.',
        });
      } else {
        toast.error('Playback failed', { description: rawMsg || 'Unknown MusicKit error — check console for details.' });
      }
      return;
    }

    // Persist to backend (non-blocking). Feb 13 2026 fix — send
    // `effectiveStart` + `playableTracks` (the FILTERED list that's
    // actually loaded in MusicKit), not the caller's raw args. When
    // any tracks were filtered by storefront resolution, the previous
    // code sent `start_index: startIndex` + `tracks: trackObjects`,
    // which meant backend's `current_index` pointed at the caller's
    // clicked index in the ORIGINAL list while frontend was actually
    // playing what's at `effectiveStart` in the FILTERED list. Later
    // stall-recovery paths would then read the wrong index and replay
    // the wrong track.
    try {
      await axios.post(`${API}/queue/load`, {
        tracks: playableTracks,
        start_index: effectiveStart,
        playlist_id: opts.playlistId || null,
        playlist_type: opts.playlistType || null,
      });
    } catch (e) {
      console.warn('[MusicContext] queue/load sync failed:', e?.message);
    }
  }, [killFade, resolveAppleId, setAppleQueue, deviceEnabled, isMasterRef, acquireRecovery, releaseRecovery]);

  const playSong = useCallback(async (idOrTrack) => {
    const track = typeof idOrTrack === 'string' ? { id: idOrTrack } : idOrTrack;
    return playTracks([track], 0);
  }, [playTracks]);

  const pause = useCallback(() => {
    playIntentRef.current = false;
    const m = musicRef.current;
    if (m) { try { m.pause(); } catch (e) { /* noop */ } }
  }, []);

  const play = useCallback(() => {
    // Master-only: refuse to start audio if another tab holds the Master
    // crown. This is what prevents the "double-tab, double-audio" Apple
    // MusicKit bug (Item 5, Feb 2026). The `enabled` guard means anonymous
    // routes still play — only authed venue tabs participate in the
    // multi-device dance.
    if (deviceEnabled && !isMasterRef.current) {
      toast.info('This tab is not the audio Master', {
        description: 'Click "Take Over" in the booth to control playback from here.',
      });
      return;
    }
    playIntentRef.current = true;
    startKeepAlive();
    armSilentAudio();
    const m = musicRef.current;
    if (m) { try { m.play(); } catch (e) { /* noop */ } }
  }, [deviceEnabled, isMasterRef]);

  // Manual skip helper — used by both `nextTrack` and `previousTrack` to
  // fully reset in-flight crossfade/prewarm/stall state so the auto-
  // crossfade tick and the ENDED handler don't race the manual advance.
  // Returns a promise that resolves once MusicKit is playing the new track.
  const _resetSkipState = useCallback(() => {
    killFade();
    setTransition(null);
    queueLockRef.current = false;
    // Reset per-track dedupe refs so the polling tick doesn't think the
    // new song has already been prewarmed / crossfaded.
    crossfadeTriggeredForIdRef.current = null;
    washTriggeredForIdRef.current = null;
    stallCrossfadeFiredForIdRef.current = null;
    prewarmSucceededForIdRef.current = null;
    stalledTicksRef.current = 0;
    lastPlayheadCurRef.current = 0;
    // Suppress the ENDED handler's own /queue/next fire for the next 8s —
    // MusicKit will fire ENDED on the outgoing track shortly after we call
    // setAppleQueue below, and without this guard the ENDED handler would
    // double-advance the backend index.
    lastCrossfadeAdvanceAtRef.current = Date.now();
    lastEndedAdvanceAtRef.current = Date.now();
    // Clear any backend prewarm lock — the DJ's manual skip invalidates
    // whatever we pre-warmed for the previous song's "next".
    axios.post(`${API}/queue/prewarm/clear`).catch(() => {});
  }, [killFade]);

  const nextTrack = useCallback(async () => {
    if (manualSkipInFlightRef.current) {
      console.info('[Skip] nextTrack ignored — a manual skip is already in flight');
      return;
    }
    const m = musicRef.current;
    if (!m) return;
    manualSkipInFlightRef.current = true;
    _resetSkipState();
    try {
      // Ask the BACKEND for the authoritative next track first — its index
      // reflects any freshly-injected requests. Then PIN Apple's queue to
      // exactly that track so audio matches UI.
      const res = await axios.post(`${API}/queue/next`);
      const nextTrackData = res.data?.track;
      if (!nextTrackData) {
        // End of queue — hand off to the ENDED-recovery path (auto-load
        // the next scheduled slot's playlist).
        try { await handleTrackEndedRef.current?.(); } catch (_) { /* noop */ }
        return;
      }
      const appleId = looksLikeAppleId(nextTrackData.id)
        ? nextTrackData.id
        : await resolveAppleId(nextTrackData);
      if (appleId) {
        await setAppleQueue([appleId], 0, { tracksById: { [String(appleId)]: nextTrackData } });
        await m.play();
      } else {
        try { await m.skipToNextItem(); } catch (_) { /* noop */ }
      }
      setCurrentTrack(nextTrackData);
      setQueueIndex(res.data.current_index);
      if (activeSlotRef.current === 'A') setSlotATrack(nextTrackData);
      else setSlotBTrack(nextTrackData);
      await refreshQueue();
    } catch (e) {
      console.warn('[MusicContext] nextTrack failed:', e?.message);
      // Guarantee playback state is not left frozen — try a best-effort
      // resume so a subsequent user tap has a running player.
      try { await m.play?.(); } catch (_) { /* noop */ }
    } finally {
      manualSkipInFlightRef.current = false;
    }
  }, [_resetSkipState, refreshQueue, resolveAppleId, setAppleQueue]);

  const previousTrack = useCallback(async () => {
    if (manualSkipInFlightRef.current) {
      console.info('[Skip] previousTrack ignored — a manual skip is already in flight');
      return;
    }
    const m = musicRef.current;
    if (!m) return;
    manualSkipInFlightRef.current = true;
    _resetSkipState();
    try {
      // Backend authoritative rewind — decrements current_index and returns
      // the track at the new index. We can't rely on MusicKit's own
      // skipToPreviousItem because after every crossfade our queue rebuild
      // sets MusicKit's internal queue to a single item (position 0), so
      // there is nothing to skip back to inside MusicKit itself.
      const res = await axios.post(`${API}/queue/previous`);
      const prevTrackData = res.data?.track;
      if (!prevTrackData || res.data?.at_start) {
        // Already at start — just restart the current track from 0.
        try { m.currentPlaybackTime = 0; } catch (_) { /* noop */ }
        try { await m.play(); } catch (_) { /* noop */ }
        await refreshQueue();
        return;
      }
      const appleId = looksLikeAppleId(prevTrackData.id)
        ? prevTrackData.id
        : await resolveAppleId(prevTrackData);
      if (appleId) {
        await setAppleQueue([appleId], 0);
        await m.play();
      } else {
        try { await m.skipToPreviousItem(); } catch (_) { /* noop */ }
      }
      setCurrentTrack(prevTrackData);
      setQueueIndex(res.data.current_index);
      if (activeSlotRef.current === 'A') setSlotATrack(prevTrackData);
      else setSlotBTrack(prevTrackData);
      await refreshQueue();
    } catch (e) {
      console.warn('[MusicContext] previousTrack failed:', e?.message);
      try { await m.play?.(); } catch (_) { /* noop */ }
    } finally {
      manualSkipInFlightRef.current = false;
    }
  }, [_resetSkipState, refreshQueue, resolveAppleId, setAppleQueue]);

  // Insert a track immediately after current via backend, then sync MusicKit queue.
  const injectNext = useCallback(async (track) => {
    try {
      const res = await axios.post(`${API}/queue/inject`, { track });
      const playNow = !!res.data?.play_now;

      // Resolve the Apple ID up-front.
      const appleId = looksLikeAppleId(track.id) ? track.id : await resolveAppleId(track);
      const m = musicRef.current;

      if (playNow && m && appleId) {
        await setAppleQueue([appleId], 0, { tracksById: { [String(appleId)]: track } });
        await m.play();
        setCurrentTrack(track);
        setShowPlayer(true);
        if (activeSlotRef.current === 'A') setSlotATrack(track);
        else setSlotBTrack(track);
      } else if (m && appleId && typeof m.playNext === 'function') {
        // MusicKit's playNext inserts an item right after the now-playing one.
        try { await m.playNext({ song: appleId }); }
        catch (e) { console.warn('[MusicContext] playNext failed:', e?.message); }
      }

      setNextUp(track);
      await refreshQueue();
      return res.data;
    } catch (error) {
      console.error('[MusicContext] Inject error:', error);
    }
  }, [resolveAppleId, setAppleQueue, refreshQueue]);

  const syncQueue = useCallback(async (newTracks) => {
    try {
      const res = await axios.post(`${API}/queue/sync`, {
        tracks: newTracks,
        current_track_id: currentTrack?.id || null,
      });
      if (res.data?.synced) {
        setQueue(newTracks);
        setQueueIndex(res.data.current_index);
        setNextUp(res.data.next_track || null);
      }
      return res.data;
    } catch (error) {
      console.error('[MusicContext] Sync queue error:', error);
    }
  }, [currentTrack]);

  // === Simulated crossfade ===
  // Apple's DRM blocks true dual-channel overlap. We fade the outgoing
  // over T-8s → T-5s (100→40%), hard-cut at T-5s, hand off via
  // skipToNextItem, and fade the incoming 60→100% over T-5s → T-3s.
  const startCrossfade = useCallback(async () => {
    if (queueLockRef.current || fadeIntervalRef.current) return;
    const m = musicRef.current;
    if (!m) return;
    queueLockRef.current = true;
    try {
      const qRes = await axios.get(`${API}/queue/current`);
      const nextTrackData = qRes.data.next_track;
      if (!nextTrackData) { queueLockRef.current = false; return; }

      // Rebuild MusicKit's queue from authoritative backend state (the
      // remaining tracks from `next_track` onwards). We deliberately do NOT
      // branch on any "is the queue intact?" heuristic — always rebuild.
      // Predictable ~2-3s DRM load window on every crossfade, on every
      // platform. The rebuild also suppresses MusicKit's ENDED event for
      // the outgoing track so the ENDED handler doesn't double-fire an
      // /api/queue/next advance (the TV-double-advance regression cause).
      const nextAppleIdFromBackend = looksLikeAppleId(nextTrackData.id)
        ? nextTrackData.id
        : (nextTrackData.apple_id || null);

      console.info('[Crossfade] T-8s trigger | fade curve 100→40 over 3s, hand-off @ T-5s, incoming 60→100 over 2s | prewarmed=',
        prewarmSucceededForIdRef.current === nowPlayingIdRef.current, '| nextBackendId=', nextAppleIdFromBackend);

      setCrossfadeActive(true);
      setTransition({ track: nextTrackData, countdown: Math.ceil(FADE_WINDOW_MS / 1000) });

      let step = 0;
      let advanced = false;

      fadeIntervalRef.current = createBackgroundInterval(() => {
        step++;
        const elapsed = step * FADE_STEP_MS; // ms since crossfade fired (T-8s + elapsed)
        try {
          if (elapsed < HANDOFF_AT_MS) {
            // ===== Phase 1: fade-out on outgoing (T-8s → T-5s) =====
            // -20% per second. secs=0 → 1.0, secs=1 → 0.8, secs=2 → 0.6, secs≈3 → 0.4
            const secs = elapsed / 1000;
            m.volume = Math.max(0.4, 1.0 - secs * 0.2);
          } else if (elapsed < FADE_WINDOW_MS) {
            // ===== Phase 3: fade-in on incoming (T-5s → T-3s) =====
            // Starts at 60% at the hand-off boundary, +20% per second up to 100%.
            const secsAfter = (elapsed - HANDOFF_AT_MS) / 1000;
            m.volume = Math.min(1.0, INCOMING_START_VOL + secsAfter * 0.2);
          } else {
            m.volume = 1.0;
          }
        } catch (e) { /* noop */ }

        // Countdown = seconds remaining in the choreography (5 → 0).
        const remainingS = Math.max(0, Math.ceil((FADE_WINDOW_MS - elapsed) / 1000));
        setTransition(prev => prev ? { ...prev, countdown: remainingS } : null);

        // ===== Phase 2: hand-off at T-5s (elapsed = HANDOFF_AT_MS) =====
        if (!advanced && elapsed >= HANDOFF_AT_MS) {
          advanced = true;
          activeSlotRef.current = activeSlotRef.current === 'A' ? 'B' : 'A';
          setCurrentTrack(nextTrackData);
          if (activeSlotRef.current === 'A') { setSlotATrack(nextTrackData); setSlotBTrack(null); }
          else { setSlotBTrack(nextTrackData); setSlotATrack(null); }
          // Force incoming volume floor to 60% immediately so the swap
          // never audibly starts from 40% (the outgoing's last value).
          try { m.volume = INCOMING_START_VOL; } catch (e) { /* noop */ }
          (async () => {
            try {
              // ===== Fast-path: MusicKit already has the next track buffered =====
              // The prewarm block (T-25s → T-23s primary, T-15/T-13 secondary,
              // T-10/T-8 backup) called `queue.append` for the next track's
              // Apple ID. If any window succeeded, MusicKit has already
              // fetched the DRM license + audio buffer for position N+1.
              // `skipToNextItem()` becomes a pointer move — hand-off in
              // ~200-500ms instead of the 4-6s cold-fetch of a rebuild.
              // THIS is the "Deck B pre-loaded, cut over instantly" model
              // the host has been asking for since morning; it's Apple's
              // internal queue acting as Deck B (there is no true dual
              // audio engine — Apple's DRM is single-instance per window).
              //
              // We try skipToNextItem FIRST and fall back to the queue
              // rebuild only if it fails, MusicKit's queue is exhausted,
              // OR MusicKit's next item no longer matches the backend's
              // next_track (a guest submitted an auto-accept request
              // between prewarm and now, so the OLD appended track is
              // stale and would play the wrong song).
              let handedOff = false;
              try {
                const qLen = m.queue?.length ?? 0;
                const qPos = m.queue?.position ?? 0;
                // MusicKit's prewarmed next item may be STALE. When an
                // auto-accepted guest request lands between the prewarm
                // (T-20s) and the crossfade fire (T-1.2s), the backend
                // inserts it at `current_index + 1`, displacing whatever
                // we prewarmed. `skipToNextItem` would then play the
                // wrong song (the old prewarmed one instead of the
                // guest's request). Detect the mismatch and force the
                // rebuild path — one 4-5s DRM cold-fetch is the correct
                // cost to pay so the guest's song plays NEXT.
                const mkitNextItem = qLen > qPos + 1
                  ? (m.queue.items?.[qPos + 1] || m.queue.item?.(qPos + 1))
                  : null;
                const mkitNextId = mkitNextItem?.id || mkitNextItem?.catalogId || null;
                const nextMatchesBackend = !!(
                  mkitNextId && nextAppleIdFromBackend && mkitNextId === nextAppleIdFromBackend
                );
                if (qLen > qPos + 1 && nextMatchesBackend) {
                  await m.skipToNextItem();
                  console.info('[Crossfade] fast-path skipToNextItem — Deck B was pre-buffered',
                    '| backendNext=', nextAppleIdFromBackend);
                  handedOff = true;
                  // Fast-path succeeded → the queue is healthy right now.
                  // Reset the consecutive slow-path counter so telemetry
                  // reflects the current state.
                  try {
                    queueHealthRef.current.slowPathCount = 0;
                    window.__musicboxQueueHealth = { ...queueHealthRef.current };
                  } catch { /* noop */ }
                } else if (qLen > qPos + 1 && !nextMatchesBackend) {
                  console.info('[Crossfade] fast-path SKIPPED — MusicKit next=', mkitNextId,
                    'but backend next=', nextAppleIdFromBackend,
                    '(guest inject during prewarm window) — rebuilding to play guest song');
                }
              } catch (skipErr) {
                console.warn('[Crossfade] skipToNextItem failed, falling back to rebuild:', skipErr?.message);
              }

              if (!handedOff) {
                // ===== Fallback: queue rebuild (~4-6s Apple DRM cold-fetch) =====
                // Fires when prewarm append didn't take (rare) or MusicKit's
                // queue was exhausted (long-idle session). Telemetry: bump the
                // slow-path counter so the Queue-Depth Watchdog + Console
                // health card can surface the "MusicKit is wedging" pattern.
                try {
                  queueHealthRef.current.slowPathCount = (queueHealthRef.current.slowPathCount || 0) + 1;
                  window.__musicboxQueueHealth = { ...queueHealthRef.current };
                } catch { /* noop */ }
                const tracksFromBackend = qRes.data.tracks || [];
                const currentIdx = qRes.data.current_index ?? 0;
                const remaining = tracksFromBackend.slice(currentIdx + 1);
                // Feb 14 2026 fix — was `.slice(0, 10)`. The 10-track cap
                // was the direct cause of the "10 songs then it stops /
                // repeats the same 10" field report (Cafe Strokes tablet,
                // Feb 14 2026): the FIRST rebuild fired mid-playlist
                // REPLACED Apple's 49-song queue with only 10 tracks. Once
                // those 10 exhausted via skipToNextItem, MusicKit's queue
                // was empty and the engine wedged; user restarts replayed
                // the same 10 from position 0. Load up to 100 (MusicKit's
                // practical safe limit) so the engine has enough runway
                // to auto-advance for the rest of the set.
                const trimmed = remaining.slice(0, 100);
                const resolvedRemainingIds = await Promise.all(
                  trimmed.map(async (t) => (looksLikeAppleId(t.id) ? t.id : await resolveAppleId(t).catch(() => null))),
                );
                const ids = resolvedRemainingIds.filter(Boolean);
                if (ids.length) {
                  // Build tracksById so setAppleQueue's self-healer can
                  // re-resolve any region-locked IDs against the live
                  // storefront rather than hard-failing the rebuild.
                  const rebuildTracksById = {};
                  trimmed.forEach((t, i) => {
                    const rid = resolvedRemainingIds[i];
                    if (rid) rebuildTracksById[String(rid)] = t;
                  });
                  // ===== Direct-swap rebuild (Feb 2026 per host feedback) =====
                  // Client reported 7-10s silence on the slow-path. Root cause:
                  // the previous rebuild called `pause() → setQueue → play()`
                  // which added a hard silent gap on top of Apple's DRM cold-
                  // fetch. `noPause: true` lets setQueue's implicit stop do
                  // the swap — "just play song 2, MusicKit auto-stops song 1."
                  await setAppleQueue(ids, 0, { tracksById: rebuildTracksById, noPause: true });
                  await m.play();
                  // VERIFY audio actually started — do NOT assume success.
                  // setQueue+play can resolve while playback silently never
                  // reaches PLAYING (the guest-inject "loads but doesn't play"
                  // bug). Tolerates the 4-6s DRM cold-fetch; fails fast on a
                  // genuine silent stall instead of waiting out the +15s
                  // overrun watchdog.
                  handedOff = await waitForPlaybackStart(m, 6000);
                  if (!handedOff) {
                    console.warn('[Crossfade] rebuild play() never reached PLAYING — retrying play() once');
                    try { await m.play(); } catch (_) { /* noop */ }
                    handedOff = await waitForPlaybackStart(m, 2500);
                  }
                  console.info('[Crossfade] rebuild-path direct-swap setAppleQueue (noPause) —',
                    ids.length, 'tracks | audioStarted=', handedOff);
                  if (!handedOff) {
                    reportPlaybackStall('crossfade_rebuild_silent_play', {
                      tracks: ids.length, first_track_id: ids[0],
                    });
                  }
                } else {
                  // ===== Feb 2026 RCA (production stall) =====
                  // The old branch here silently called `m.skipToNextItem()`
                  // which no-ops on an exhausted / mismatched MusicKit queue
                  // and left audio stopped — while the code below still
                  // advanced the backend pointer. Result: "playlist stops
                  // mid-set, queue shows remaining tracks." NEVER again.
                  //
                  // If we have ZERO resolvable ids for the rebuild, don't
                  // pretend we handed off. Surface a telemetry ping and
                  // let the outer finally block DECLINE to advance
                  // /queue/next — the ENDED handler / watchdog will retry
                  // on the next tick against a fresh /queue/current pull.
                  console.error(
                    '[Crossfade] rebuild has zero resolvable ids —',
                    'trimmed=', trimmed.length,
                    'resolved=', resolvedRemainingIds.filter(Boolean).length,
                  );
                  reportPlaybackStall('crossfade_zero_resolved_ids', {
                    trimmed_count: trimmed.length,
                    resolved_count: resolvedRemainingIds.filter(Boolean).length,
                    first_track_id: trimmed[0]?.id,
                  });
                  handedOff = false;
                }
              }

              // ===== Feb 2026 fix (production stall RCA) =====
              // Only advance backend's current_index AFTER the client has
              // verifiably handed off audio. If handoff failed, DO NOT move
              // the server pointer — that decoupling is the root cause of
              // "queue shows remaining tracks but audio stopped." The next
              // ENDED tick / watchdog will retry from the correct index.
              if (handedOff) {
                try {
                  await axios.post(`${API}/queue/next`);
                  lastCrossfadeAdvanceAtRef.current = Date.now();
                } catch (advErr) {
                  console.warn('[MusicContext] crossfade backend advance failed:', advErr?.message);
                }
              } else {
                console.warn(
                  '[Crossfade] handoff did NOT succeed — skipping /queue/next.',
                  'Backend pointer left at current index — scheduling FAST recovery',
                  'so the just-injected guest song is retried (not skipped).',
                );
                reportPlaybackStall('crossfade_handoff_failed', {});
                // Fast recovery: the backend pointer still points at the
                // track BEFORE the guest inject, so /queue/current's
                // next_track is still the guest song. Rebuild from fresh
                // backend state the moment the lock releases below, instead
                // of waiting out the +15s overrun watchdog (the ~19s silence
                // + "skipped my song" guests reported). handleTrackEnded owns
                // its own queue-lock handshake, so defer past this finally.
                setTimeout(() => {
                  try { handleTrackEndedRef.current?.(); } catch (_) { /* noop */ }
                }, 400);
              }
            } catch (e) {
              console.warn('[MusicContext] crossfade advance failed:', e?.message,
                           '— falling back to handleTrackEnded for full backend recovery.');
              try { handleTrackEndedRef.current?.(); } catch (_) { /* noop */ }
            } finally {
              // Release the queue lock ONLY after the rebuild's async work
              // (pause → setQueue → play → backend advance) is fully done.
              //
              // Prior bug: we released `queueLockRef` at fade step 12 (~1.2s)
              // while Apple's DRM cold-fetch of the new track was still in
              // flight (3-5s). During that gap MusicKit sometimes fired an
              // ENDED event for the outgoing track that then satisfied
              // `atEnd` (its internal queue was momentarily at position N
              // of length N mid-transition), which called `handleTrackEnded`
              // → advanced the backend a SECOND time → replaced MusicKit's
              // queue with the track AFTER the guest song → guest song
              // loaded on deck but was immediately skipped. Symptom
              // reported by client Feb 13: "loads on deck, then plays the
              // next playlist song". Fixed by keeping the lock true through
              // the entire rebuild.
              queueLockRef.current = false;
              // Belt: also explicit clear of the prewarm lock in case the
              // backend advance failed silently.
              axios.post(`${API}/queue/prewarm/clear`).catch(() => {});
              refreshQueue();
            }
          })();
        }

        if (elapsed >= FADE_WINDOW_MS) {
          if (fadeIntervalRef.current) {
            fadeIntervalRef.current.clear();
            fadeIntervalRef.current = null;
          }
          try { m.volume = 1.0; } catch (e) { /* noop */ }
          setCrossfadeActive(false);
          setTransition(null);
          // NOTE: `queueLockRef.current` is NOT released here — the
          // async rebuild block above owns its lifecycle. Releasing it
          // now would open a race window where MusicKit's stray ENDED
          // events during Apple's DRM cold-fetch call `handleTrackEnded`
          // and skip past the just-loaded guest track. See fix comment
          // in the async block's `finally`.
        }
      }, FADE_STEP_MS);
    } catch (error) {
      console.error('[MusicContext] Crossfade error:', error);
      setCrossfadeActive(false);
      setTransition(null);
      queueLockRef.current = false;
      const m2 = musicRef.current;
      if (m2) { try { m2.volume = 1.0; } catch (e) { /* noop */ } }
    }
  }, [refreshQueue, resolveAppleId, setAppleQueue]);

  const startCrossfadeRef = useRef(startCrossfade);
  useEffect(() => { startCrossfadeRef.current = startCrossfade; }, [startCrossfade]);

  // ENDED-recovery — used when MusicKit's natural auto-advance doesn't fire
  // (queue exhausted, error, etc.). When the active queue is done playing
  // and a curated/scheduled playlist was driving it, auto-advance to the
  // NEXT slot's playlist in the host's weekly timetable (morning →
  // afternoon → evening → night → next-day's morning…). This is the core
  // "set it and forget it" behaviour for a B2B venue running all day.
  const handleTrackEnded = useCallback(async () => {
    if (queueLockRef.current || fadeIntervalRef.current) return;
    // Feb 13 2026 — serialize with hardReset + emergencyHardRecover.
    // queueLockRef alone was NOT enough because the other paths never
    // checked it. Now they all share `recoveryInFlightRef` (see comment
    // at declaration). queueLockRef is preserved as an inner lock for
    // the crossfade + rebuild pipeline; recoveryInFlightRef is the
    // OUTER mutex across all three recovery entry points.
    if (!acquireRecovery('handleTrackEnded')) return;
    queueLockRef.current = true;
    // Ping the requests inbox to clear this song's card as soon as it's
    // done (or transitioning). Best-effort — never blocks the queue advance.
    try {
      const finishedTrack = currentTrackRef.current;
      const finishedId = finishedTrack && (finishedTrack.apple_id || finishedTrack.id);
      if (finishedId) {
        axios.post(`${API}/requests/mark-played`, { apple_id: String(finishedId) })
          .catch(() => { /* non-fatal — DJ UI polls anyway */ });
      }
    } catch (_) { /* noop */ }
    try {
      // ===== Sep 2026 fix (mirrors startCrossfade's Feb 2026 RCA fix) =====
      // PEEK the next track via GET /queue/current instead of advancing the
      // backend pointer up front. This handler used to call
      // `POST /queue/next` BEFORE attempting the audio hand-off, so a failed
      // hand-off left the backend pointer already moved past the stuck song
      // — "queue shows remaining tracks, audio stopped." That decoupling was
      // already fixed in the crossfade path (see `handedOff` there); this
      // mirrors the same fix here. The backend pointer is only advanced
      // AFTER the hand-off is verified below.
      const curRes = await axios.get(`${API}/queue/current`);
      let incomingTrack = curRes.data.next_track;
      let nextIndex = (curRes.data.current_index ?? 0) + 1;

      // Feb 13 2026 — poisoned-track guard.
      // If we already know the next Apple ID is unplayable on this
      // account (previous CONTENT_RESTRICTED hit this session), skip
      // it upfront instead of taking another 15s stall. This is the
      // hot loop protection: even if the queue somehow lands back on
      // a poisoned track (guest request, playlist reshuffle, etc.),
      // we bail out immediately.
      let skipHops = 0;
      while (incomingTrack && skipHops < 20) {
        const peekId = String(incomingTrack.apple_id || incomingTrack.id || '');
        if (!peekId || !poisonedAppleIdsRef.current.has(peekId)) break;
        console.warn('[handleTrackEnded] skipping poisoned track:', peekId, incomingTrack.name);
        reportPlaybackStall('poisoned_track_preempted', {
          apple_id: peekId,
          hops: skipHops + 1,
        });
        try {
          await axios.post(`${API}/queue/next`);
          const next = await axios.get(`${API}/queue/current`);
          incomingTrack = next.data.next_track;
          nextIndex = (next.data.current_index ?? 0) + 1;
        } catch (adv) {
          console.warn('[handleTrackEnded] poisoned-track skip advance failed:', adv?.message);
          break;
        }
        skipHops += 1;
      }
      // Set when the branch below has already advanced the backend pointer
      // (the "queue looked exhausted" path) — skips the gated advance later.
      let backendAlreadyAdvanced = false;
      if (!incomingTrack) {
        // Queue exhausted — no hand-off decision to make, safe to advance /
        // mark-inactive immediately (matches previous behaviour exactly).
        const advRes = await axios.post(`${API}/queue/next`);
        if (advRes.data.finished || !advRes.data.track) {
          // First try: next slot AFTER the playlist we just finished — but
          // ONLY within today's timetable. The backend /next-after no longer
          // wraps into the next day.
          const fromPlaylistId = currentPlaylistIdRef.current;
          if (fromPlaylistId) {
            try {
              const nextRes = await axios.get(`${API}/playlists/next-after`, {
                params: { playlist_id: fromPlaylistId },
              });
              const nextPl = nextRes.data?.playlist;
              if (nextPl?.id) {
                const trackRes = await axios.get(`${API}/playlists/curated/${nextPl.id}`);
                const tracks = (trackRes.data?.tracks || []).filter(t => t?.id);
                if (tracks.length > 0) {
                  queueLockRef.current = false;
                  await playTracks(tracks, 0, { playlistId: nextPl.id });
                  const slotLabel = nextRes.data?.slot ? ` (${nextRes.data.slot})` : '';
                  toast.success(`Up next${slotLabel}: ${nextPl.name}`);
                  return;
                }
              }
            } catch (nextErr) {
              console.warn('[MusicContext] next-after lookup failed:', nextErr?.message);
            }
            // The day's schedule is complete (next-after returned null) — STOP.
            // User requirement (Oct 2026): once the day's playlists are over,
            // playback must stop and NOT roll into the next day's morning or
            // loop the current slot. The next day's slots auto-start when the
            // DJ next opens/plays the console.
            console.info('[MusicContext] Day schedule complete — stopping playback (no next-day auto-start).');
            queueLockRef.current = false;
            setIsPlaying(false);
            toast.info("Today's scheduled playlists are complete. Playback stopped.");
            return;
          }
          // No scheduled context (ad-hoc play): one-time auto-load of the
          // venue's current real-time slot. After that playlist ends it has a
          // playlistId, so the scheduled branch above governs — which stops at
          // end of day rather than looping.
          try {
            const slotRes = await axios.get(`${API}/playlists/current-slot`);
            const slotPl = slotRes.data?.playlist;
            if (slotPl?.id) {
              const trackRes = await axios.get(`${API}/playlists/curated/${slotPl.id}`);
              const tracks = (trackRes.data?.tracks || []).filter(t => t?.id);
              if (tracks.length > 0) {
                queueLockRef.current = false;
                await playTracks(tracks, 0, { playlistId: slotPl.id });
                toast.success(`Auto-loaded: ${slotPl.name}`);
                return;
              }
            }
          } catch (slotErr) {
            console.warn('[MusicContext] auto-loop slot lookup failed:', slotErr?.message);
          }
          queueLockRef.current = false;
          setIsPlaying(false);
          return;
        }
        // Rare race: the queue was mutated (e.g. a guest song injected)
        // between the peek above and this advance call, so the peek's
        // "exhausted" read was stale. The advance above already moved the
        // backend pointer — hand off to what it actually returned instead
        // of the stale (null) peek, and don't advance again below.
        incomingTrack = advRes.data.track;
        nextIndex = advRes.data.current_index;
        backendAlreadyAdvanced = true;
      }
      const appleId = looksLikeAppleId(incomingTrack.id)
        ? incomingTrack.id
        : await resolveAppleId(incomingTrack);
      const m = musicRef.current;
      // Tracks whether audio was verifiably handed off — gates the backend
      // `/queue/next` advance below, same decoupling fix as startCrossfade.
      let handedOff = false;
      // Oct 3 2026 — late-success guard: if MusicKit is ALREADY on the
      // track we are about to load (a previous "silent" handoff finished
      // loading after we gave up), do not reload it — just mark handed off
      // so the backend pointer catches up below. This is the "same song
      // plays twice" bug from Poptates Versova.
      const alreadyOnIt = (() => {
        try {
          const npId = String(m?.nowPlayingItem?.id || '');
          if (!appleId || npId !== String(appleId)) return false;
          const pos = Number(m?.currentPlaybackTime ?? 0);
          return m.playbackState === 2 || pos > 1;
        } catch { return false; }
      })();
      if (m && appleId && alreadyOnIt) {
        console.info('[handleTrackEnded] MusicKit already on next track', String(appleId), '— advancing pointer only');
        reportPlaybackStall('handoff_late_success_reconciled', { apple_id: String(appleId), current_index: nextIndex, via: 'handleTrackEnded' });
        if (m.playbackState !== 2) { try { await m.play(); } catch { /* noop */ } }
        handedOff = true;
      } else if (m && appleId) {
        // ===== Fast-path: MusicKit queue already has the next item loaded =====
        // If any of the 3 prewarm windows landed (T-25/T-15/T-10), the next
        // track's DRM license + audio buffer are ALREADY in MusicKit's queue
        // at position N+1. `skipToNextItem()` becomes a ~200-500ms pointer
        // move instead of the 4-6s DRM cold-fetch of setQueue().
        //
        // Feb 2026 root-cause for the "4-6s silence after song end" report:
        // when the T-8s crossfade window is missed (background-throttled
        // tab, transient queueLock, or fadeInterval sentinel stuck), we
        // fell through to this handler which used to ALWAYS do the slow
        // rebuild — even when prewarm had succeeded. That was a wasted
        // opportunity. Check the queue first, use fast-path when we can.
        let usedFastPath = false;
        try {
          const q = m.playbackQueue || m.queue;
          const qItems = q && q.items ? q.items : [];
          const qPos = typeof (q && q.position) === 'number' ? q.position : -1;
          const nextInQueue = qPos >= 0 && qPos + 1 < qItems.length ? qItems[qPos + 1] : null;
          const nextIdInQueue = nextInQueue && String(nextInQueue.id || nextInQueue.container?.id || '');
          if (nextIdInQueue && nextIdInQueue === String(appleId) && typeof m.skipToNextItem === 'function') {
            console.info('[handleTrackEnded] fast-path: skipToNextItem — next track already in queue @ pos', qPos + 1);
            await m.skipToNextItem();
            usedFastPath = true;
          }
        } catch (e) {
          console.warn('[handleTrackEnded] fast-path probe failed:', e?.message);
        }
        if (!usedFastPath) {
          // Slow-path rebuild. `noPause: true` lets setQueue's implicit
          // stop do the swap instead of an explicit pause() adding ~200ms
          // of hard silence before Apple's DRM cold-fetch even begins.
          console.info('[handleTrackEnded] slow-path: setQueue rebuild (prewarm missed)');
          await setAppleQueue([appleId], 0, { tracksById: { [String(appleId)]: incomingTrack }, noPause: true });
          await m.play();
        }
        // ===== Feb 2026 fix (production stall RCA) =====
        // Verify audio ACTUALLY started. This is the same check the ENDED
        // handler applies for MusicKit's native advance. If playbackState
        // never reaches 2 (PLAYING) within 2s, the handoff silently
        // stalled — surface telemetry so we see this class of bug live
        // instead of finding out via customer complaints.
        // Oct 3 2026 — be patient while the SDK is still WORKING (1=loading,
        // 6=seeking, 7=stalled, 8=waiting): a cold DRM fetch on slow venue
        // Wi-Fi routinely takes 4–8 s. Bailing at 2 s and then treating the
        // eventual success as a failure is what caused the repeat loop.
        const SDK_WORKING = new Set([1, 6, 7, 8]);
        const audible = await new Promise((resolve) => {
          let elapsed = 0;
          const iv = setInterval(() => {
            if (m.playbackState === 2) { clearInterval(iv); resolve(true); return; }
            elapsed += 100;
            const working = SDK_WORKING.has(m.playbackState);
            if (elapsed >= 12000 || (elapsed >= 2000 && !working)) { clearInterval(iv); resolve(false); }
          }, 100);
        });
        if (!audible) {
          console.error(
            '[handleTrackEnded] handoff completed but playbackState never reached 2 —',
            'track=', String(appleId),
            'fastPath=', usedFastPath,
          );
          reportPlaybackStall('handletrackended_handoff_silent', {
            apple_id: String(appleId),
            fast_path: usedFastPath,
            current_index: nextIndex,
          });
          // Field data (Feb 8 2026, bengaluru.yazu@gmail.com): this fires
          // 13x in 48h with only a single m.play() retry — clearly not
          // aggressive enough. Retry 3 times with backoff before we
          // let the watchdog cascade fire. Each attempt is cheap (a
          // single SDK call), the win is huge: catching the ~30% of
          // handoff-silents that clear on a second/third nudge.
          let recoveredOnRetry = false;
          for (let hoAttempt = 1; hoAttempt <= 3; hoAttempt += 1) {
            try { await m.play?.(); } catch { /* noop */ }
            // Wait 500ms/800ms/1200ms between checks — Apple's SDK
            // typically settles within 1s if the handoff succeeds.
            const settle = 300 + hoAttempt * 200 + (hoAttempt === 3 ? 400 : 0);
            const recovered = await new Promise((res) => {
              let elapsed = 0;
              const iv = setInterval(() => {
                if (m.playbackState === 2) { clearInterval(iv); res(true); return; }
                elapsed += 100;
                if (elapsed >= settle) { clearInterval(iv); res(false); }
              }, 100);
            });
            if (recovered) {
              console.info(`[handleTrackEnded] handoff recovered on retry ${hoAttempt}`);
              recoveredOnRetry = true;
              break;
            }
          }
          handedOff = recoveredOnRetry;
        } else {
          handedOff = true;
        }
      } else if (m && !appleId) {
        // No resolvable Apple id for the next track — this used to
        // silently strand the queue (Feb 2026 production RCA). Surface
        // telemetry + let the watchdog retry on the next tick against a
        // fresh /queue/current pull.
        console.error(
          '[handleTrackEnded] next track has no resolvable Apple id —',
          'incoming.id=', incomingTrack.id,
          'name=', incomingTrack.name,
        );
        reportPlaybackStall('handletrackended_no_apple_id', {
          incoming_id: String(incomingTrack.id || ''),
          incoming_name: String(incomingTrack.name || ''),
          current_index: nextIndex,
        });
      }

      // ===== Sep 2026 fix (mirrors startCrossfade's Feb 2026 RCA fix) =====
      // Only advance the backend's current_index AFTER the client has
      // verifiably handed off audio. If handoff failed, DO NOT move the
      // server pointer — the next ENDED tick / watchdog will retry against
      // this SAME `/queue/current` peek instead of skipping past it.
      if (backendAlreadyAdvanced) {
        // Rare race path above already advanced the backend — nothing more
        // to do here regardless of hand-off outcome.
        consecutiveHandoffFailsRef.current = { appleId: null, count: 0 };
        pendingHandoffRef.current = null;
      } else if (handedOff) {
        pendingHandoffRef.current = null;
        try {
          await axios.post(`${API}/queue/next`);
          consecutiveHandoffFailsRef.current = { appleId: null, count: 0 };
        } catch (advErr) {
          console.warn('[MusicContext] handleTrackEnded backend advance failed:', advErr?.message);
        }
      } else {
        // Remember the attempt so a late success can still move the pointer.
        if (appleId) pendingHandoffRef.current = { appleId: String(appleId), nextIndex, at: Date.now() };
        // Hand-off failed. Track how many times we've failed on THIS
        // specific next-track. Same track 3x in a row → the track
        // itself is broken (Apple 404 / DRM / removed from catalogue);
        // retrying it forever produces the "same song repeated"
        // client complaint from Sep 2026. Skip past it.
        //
        // Feb 12 2026 refinement — do NOT count network-flaky handoff
        // failures. If the venue's WiFi blipped during the transition,
        // Apple's DRM manifest fetch fails, our own /queue/current
        // fails, and MusicKit's play() never reaches state 2 — none of
        // which means the target track is actually broken. Log it under
        // a distinct reason so Forensics still shows the pattern, but
        // don't advance the "broken" counter. When the state watchdog
        // fires again after network restore, we retry the SAME track.
        const netFlaky = isNetworkFlaky();
        if (netFlaky) {
          console.warn('[handleTrackEnded] Handoff failed while network flaky — not counting as broken track');
          reportPlaybackStall('handoff_deferred_network_flaky', {
            apple_id: String(appleId || ''),
            flaky_until_ms_from_now: Math.max(0, networkFlakyRef.current.until - Date.now()),
          });
          // Skip the whole broken-track-counter branch; UI state
          // updates still happen below.
        } else {
        const bad = String(appleId || '');
        const prev = consecutiveHandoffFailsRef.current;
        const nextCount = prev.appleId === bad ? prev.count + 1 : 1;
        consecutiveHandoffFailsRef.current = { appleId: bad, count: nextCount };

        if (nextCount >= 2) {
          // Reduced from 3 → 2 on Feb 12 2026. Two consecutive automatic
          // handoff failures on the SAME apple_id + a manual-skip-works
          // pattern (Yazu Bangalore, Billie Jean → Tum Mile) = the SDK
          // is refusing the setQueue([single_id]) rebuild for this track
          // but happily plays it via nextTrack/skipToNextItem. Don't
          // torture the DJ with a THIRD attempt that just makes them
          // hear the current song a THIRD time — skip past it now.
          console.warn(
            '[handleTrackEnded] Same track failed 2× in a row —',
            'skipping past it. bad_apple_id=', bad,
          );
          reportPlaybackStall('handoff_skip_bad_track', {
            apple_id: bad, consecutive_fails: nextCount,
          });
          try {
            await axios.post(`${API}/queue/next`); // advance past the bad slot
          } catch (advErr) {
            console.warn('[MusicContext] skip-bad-track advance failed:', advErr?.message);
          }
          consecutiveHandoffFailsRef.current = { appleId: null, count: 0 };
          pendingHandoffRef.current = null;
          // Immediately retry the ENDED path against the NEW next-track
          // so the DJ doesn't wait for the state watchdog's 12s window
          // to notice the gap. This is safe: queueLock is released
          // below in the finally-equivalent (line ~1949), and the
          // re-entry passes through the standard fast-path check.
          setTimeout(() => {
            try {
              if (playIntentRef.current && !queueLockRef.current) {
                handleTrackEndedRef.current?.();
              }
            } catch { /* noop */ }
          }, 150);
        } else {
          console.warn(
            '[handleTrackEnded] hand-off did NOT succeed — skipping /queue/next.',
            'Backend pointer left at current index for retry.',
            `(${nextCount}/3 consecutive fails on this track)`,
          );
          reportPlaybackStall('handletrackended_advance_skipped', {
            apple_id: bad,
            current_index: nextIndex,
            consecutive_fails: nextCount,
          });
        }
        }  // end network-flaky else
      }

      setCurrentTrack(incomingTrack);
      setQueueIndex(nextIndex);
      if (activeSlotRef.current === 'A') setSlotATrack(incomingTrack);
      else setSlotBTrack(incomingTrack);
      setProgressMs(0);
      setTransition(null);
      setCrossfadeActive(false);
      queueLockRef.current = false;
      refreshQueue();
    } catch (error) {
      console.error('[MusicContext] handleTrackEnded error:', error);
      // ===== Feb 13 2026 fix — CONTENT_RESTRICTED handling =====
      // Field data (Jolly Johnny's Bar): "song 1 ends → tries song 2 →
      // 'playback failed please check explicit content settings' → 15s
      // hang → plays song 1 again → loop." The Apple ID is genuinely
      // unplayable on this account (Screen Time content restriction
      // that survives the user toggling "Allow Explicit" in the Music
      // app — see Apple developer forums thread 731897). Retrying
      // forever wastes 15s per cycle and the DJ has to manually skip.
      //
      // Detect the specific error, mark the Apple ID as poisoned for
      // this session, force-advance the backend past the broken slot,
      // and immediately re-fire handleTrackEnded against the new next.
      if (isContentRestrictedError(error)) {
        const badId = String(
          error?.mediaItem?.id ||
          error?.error?.mediaItem?.id ||
          nowPlayingIdRef.current ||
          ''
        );
        // Best-effort: also poison the peek's Apple ID (that's the one
        // we were TRYING to play). Retrieve from the /queue/current peek
        // one more time — cheap axios read, worth it for accuracy.
        try {
          const q = await axios.get(`${API}/queue/current`);
          const peekId = q.data?.next_track?.apple_id || q.data?.next_track?.id;
          if (peekId) poisonedAppleIdsRef.current.add(String(peekId));
        } catch { /* noop */ }
        if (badId) poisonedAppleIdsRef.current.add(badId);
        reportPlaybackStall('content_restricted_skip', {
          bad_apple_id: badId || null,
          poisoned_count: poisonedAppleIdsRef.current.size,
          err_msg: String(error?.message || error?.error?.message || '').slice(0, 200),
        });
        try {
          await axios.post(`${API}/queue/next`); // advance past the restricted track
        } catch (advErr) {
          console.warn('[MusicContext] content-restricted skip advance failed:', advErr?.message);
        }
        consecutiveHandoffFailsRef.current = { appleId: null, count: 0 };
        queueLockRef.current = false;
        // Fire the ended path again against the NEW next-track. The
        // poisoned-set guard at the top of handleTrackEnded (added
        // separately) prevents us from re-attempting the same restricted
        // Apple ID even if the queue somehow lands back on it.
        setTimeout(() => {
          if (playIntentRef.current && !queueLockRef.current) {
            try { handleTrackEndedRef.current?.(); } catch { /* noop */ }
          }
        }, 150);
        return;
      }
      // The global axios interceptor (apiInterceptor.js) already retries any
      // 401 here ONCE after a silent `/auth/refresh` — so by the time we see
      // a 401 in this catch, refresh itself also failed (session genuinely
      // dead) or the replayed call 401'd again. Previously this was
      // indistinguishable from any other stray exception: no telemetry, just
      // a console.error. Report it as its own reason so Forensics can show
      // how often a mid-set session expiry — not a hand-off bug — is what
      // actually stopped the music.
      if (error?.response?.status === 401) {
        reportPlaybackStall('handletrackended_auth_expired', {
          now_playing_id: nowPlayingIdRef.current,
          url: error?.config?.url || '',
        });
      }
      queueLockRef.current = false;
    } finally {
      // Feb 13 2026 — always release the outer recovery mutex, no
      // matter which internal path we exited through (early return,
      // successful advance, thrown error). Missing this would freeze
      // ALL recovery paths permanently.
      releaseRecovery();
    }
  }, [playTracks, refreshQueue, resolveAppleId, setAppleQueue, acquireRecovery, releaseRecovery]);

  const handleTrackEndedRef = useRef(handleTrackEnded);
  useEffect(() => { handleTrackEndedRef.current = handleTrackEnded; }, [handleTrackEnded]);
  const refreshQueueRef = useRef(refreshQueue);
  useEffect(() => { refreshQueueRef.current = refreshQueue; }, [refreshQueue]);

  // === Network-flaky detector + auto-resume on connectivity restore ==
  // Listens to the same `app:network-status` events axiosSetup.js emits
  // for the Connectivity Pill (so a "reconnecting" or "offline" in the UI
  // ALWAYS maps 1:1 to `networkFlakyRef.current.flaky === true` here).
  // On a transition back to `online` we schedule a single handleTrackEnded
  // kick to unblock any queue that stalled during the outage — the state
  // watchdog would eventually do this on its 12 s interval, but the DJ
  // gets audible feedback ~11 s faster this way.
  useEffect(() => {
    const NETWORK_FLAKY_TAIL_MS = 15_000;
    const onStatus = (evt) => {
      const s = evt?.detail?.status;
      if (!s) return;
      if (s === 'reconnecting' || s === 'offline') {
        networkFlakyRef.current = {
          flaky: true,
          until: Date.now() + NETWORK_FLAKY_TAIL_MS,
        };
        return;
      }
      if (s === 'online') {
        const wasFlaky = networkFlakyRef.current.flaky;
        // Keep the 15 s tail even on recovery — MusicKit / DRM may still
        // be recovering. We DON'T clear immediately; the watchdogs check
        // `isNetworkFlaky()` which honours the tail.
        // But we DO want to opportunistically resume playback now.
        if (wasFlaky && playIntentRef.current) {
          const m = musicRef.current;
          const paused = !m || m.playbackState !== 2;
          if (paused && !queueLockRef.current) {
            // Oct 2026 (Poptates Versova, 85 drops/evening): be GENTLE first.
            // If the SDK is merely buffering the CURRENT song (1 loading /
            // 7 stalled / 8 waiting) a `play()` nudge lets it resume where
            // it was. Jumping straight to handleTrackEnded skipped the song
            // mid-way on every blip — "it stopped in the middle".
            setTimeout(() => {
              const mk = musicRef.current;
              if (!playIntentRef.current || !mk || mk.playbackState === 2 || queueLockRef.current) return;
              const buffering = [1, 7, 8].includes(mk.playbackState);
              reportPlaybackStall('network_restore_resume_kick', {
                playback_state: mk.playbackState, gentle: buffering,
              });
              try { mk.play(); } catch { /* noop */ }
              if (!buffering) {
                try { handleTrackEndedRef.current?.(); } catch { /* noop */ }
                return;
              }
              // Escalate only if the nudge didn't bring audio back in 12 s.
              setTimeout(() => {
                const mk2 = musicRef.current;
                if (!playIntentRef.current || !mk2 || mk2.playbackState === 2 || queueLockRef.current) return;
                reportPlaybackStall('network_restore_resume_escalated', { playback_state: mk2.playbackState });
                try { handleTrackEndedRef.current?.(); } catch { /* noop */ }
              }, 12_000);
            }, 800);
          }
        }
      }
    };
    window.addEventListener(NETWORK_STATUS_EVENT, onStatus);
    return () => window.removeEventListener(NETWORK_STATUS_EVENT, onStatus);
  }, []);
  // Ref mirror of playTracks so the low-frequency watchdogs (queue
  // health, hard-reset) can call it without stale closures across
  // callback identity changes. Feb 2026 auto-recover work.
  const playTracksRef = useRef(playTracks);
  useEffect(() => { playTracksRef.current = playTracks; }, [playTracks]);

  // === Emergency MusicKit hard-recovery (Feb 12 2026 — nuclear option) ===
  // The soft ladder is:
  //   1. `.play()` retry             (isPlaying watchdog)
  //   2. `.seekTo() + .play()` nudge (silent-audio watchdog)
  //   3. `handleTrackEnded` → queue rebuild        (silent-audio watchdog step 2)
  //   4. `hardReset()` → full re-arm from backend  (playback-chain watchdog)
  //
  // Field forensics from Yazu Bangalore (Feb 12 2026, 5h window) showed
  // 12 × `silent_audio_position_frozen` + 13 × `hard_reset_reload_queue` —
  // i.e. even step 4 (full queue re-arm on the SAME MusicKit instance)
  // isn't enough on a wedged Android tab. The `<audio>` decoder MusicKit
  // spawns can survive queue rebuilds and continue reporting
  // `playbackState === 2` while emitting no audio.
  //
  // This helper is step 5 — nuke the entire MusicKit singleton (destroys
  // its `<audio>` element + drops the singleton), re-configure a fresh
  // instance (auto-reconnects using the stored MusicUserToken so it's
  // gesture-free), then re-run our init effect so all event handlers are
  // reattached to the new instance, then re-arm the queue.
  //
  // Cost: ~1.5-2s of silence on Android + a JWT fetch (cached). Never
  // fires more than once per 3 minutes.
  const emergencyHardRecoverMusicKit = useCallback(async (reason = 'unknown') => {
    const now = Date.now();
    if (now - lastHardMkResetAtRef.current < 3 * 60 * 1000) {
      console.warn('[MusicContext] emergencyHardRecoverMusicKit — throttled, last was',
                   Math.round((now - lastHardMkResetAtRef.current) / 1000), 's ago');
      return false;
    }
    // Feb 13 2026 — serialize with other recovery paths (see
    // recoveryInFlightRef comment). Prevents this from running while
    // handleTrackEnded or hardReset is mid-flight, which was the race
    // that produced double-advance + wedged MusicKit state.
    if (!acquireRecovery('emergencyHardRecoverMusicKit')) return false;
    try {
      lastHardMkResetAtRef.current = now;
      console.warn('[MusicContext] 🚨 emergencyHardRecoverMusicKit — reason:', reason);
      // Save current position from the backend queue so we can resume from
      // the exact track we were stuck on (MusicKit's internal position is
      // untrustworthy at this point).
      let resumeTracks = null;
      let resumeIdx = 0;
      let resumeMeta = { playlistId: null, playlistType: null };
      try {
        const q = await axios.get(`${API}/queue/current`);
        resumeTracks = q.data?.tracks || [];
        resumeIdx = Math.max(0, Math.min(q.data?.current_index || 0, resumeTracks.length - 1));
        resumeMeta.playlistId = q.data?.playlist_id || null;
        resumeMeta.playlistType = q.data?.playlist_type || null;
      } catch (err) {
        console.warn('[MusicContext] emergencyHardRecoverMusicKit — queue fetch failed, aborting:', err?.message);
        return false;
      }
      reportPlaybackStall('musickit_hard_reset', {
        reason,
        resume_idx: resumeIdx,
        queue_len: resumeTracks?.length || 0,
      });
      try {
        await hardResetMusicKit();
      } catch (err) {
        console.error('[MusicContext] hardResetMusicKit failed:', err);
        return false;
      }
      setMusicKitEpoch((n) => n + 1);
      // Wait for the init effect to re-run and finish attaching event
      // handlers. `getMusicKit()` inside the effect is cached at this
      // point so the effect body completes in a few ms; 600ms is generous.
      await new Promise((r) => setTimeout(r, 600));
      // Re-arm playback from the exact track we were stuck on.
      if (resumeTracks && resumeTracks.length) {
        try {
          await playTracksRef.current?.(resumeTracks, resumeIdx, resumeMeta);
          console.info('[MusicContext] 🚨 emergencyHardRecoverMusicKit — re-armed queue @ index', resumeIdx, '/', resumeTracks.length);
        } catch (err) {
          console.error('[MusicContext] emergencyHardRecoverMusicKit — playTracks failed:', err?.message);
        }
      }
      return true;
    } finally {
      releaseRecovery();
    }
  }, [acquireRecovery, releaseRecovery]);
  const emergencyHardRecoverRef = useRef(emergencyHardRecoverMusicKit);
  useEffect(() => { emergencyHardRecoverRef.current = emergencyHardRecoverMusicKit; }, [emergencyHardRecoverMusicKit]);

  const skipTransition = useCallback(() => {
    killFade();
    setTransition(null);
    queueLockRef.current = false;
  }, [killFade]);

  // === Progress polling + crossfade trigger ===
  // Web-worker timer so the tab still ticks when backgrounded. Reads MusicKit's
  // `currentPlaybackTime` and `currentPlaybackDuration` (both in seconds).
  // For the INACTIVE deck we explicitly mark its state as 'paused' (or 'idle'
  // when empty) and freeze its progress at the last known value — this keeps
  // the manual-mode DJ UX consistent: only one deck can be audible at a time
  // (MusicKit constraint), so the inactive deck must show the play icon so
  // the user can tap to switch the active deck back to it.
  useEffect(() => {
    if (!playerReady) return undefined;
    const tick = () => {
      const m = musicRef.current;
      if (!m) return;
      try {
        const cur = m.currentPlaybackTime || 0; // seconds
        const dur = m.currentPlaybackDuration || 0;
        const playingNow = m.playbackState === 2;
        setIsPlaying(playingNow);
        setProgressMs(cur * 1000);
        if (dur > 0) setDurationMs(dur * 1000);

        const active = activeSlotRef.current;
        const liveState = playingNow ? 'playing' : (m.playbackState === 3 ? 'paused' : 'idle');

        // Active slot mirrors MusicKit truth.
        if (active === 'A') {
          setSlotAProgress(cur * 1000);
          setSlotADuration(dur * 1000);
          setSlotAState(liveState);
          // Inactive slot B: 'paused' if it has a track loaded, otherwise idle.
          setSlotBState(slotBTrack ? 'paused' : 'idle');
        } else {
          setSlotBProgress(cur * 1000);
          setSlotBDuration(dur * 1000);
          setSlotBState(liveState);
          setSlotAState(slotATrack ? 'paused' : 'idle');
        }

        // === Next-track pre-warm (triple-window, verified) ===
        // Three windows fire per song, first-success-wins:
        //   PRIMARY    — T-25..T-23s : catalog GET + queue.append + backend lock.
        //   SECONDARY  — T-15..T-13s : only fires if PRIMARY didn't verifiably grow queue.length.
        //   BACKUP     — T-10..T-8s  : final retry — if it lands here we're
        //                              still ahead of the T-8 crossfade trigger.
        //
        // Feb 2026 tuning per production client feedback ("7-10s silence
        // between songs"): PRIMARY was T-20 → moved to T-25 so Apple's DRM
        // license fetch (documented up to 4s on Widevine tablets, up to 6s
        // on cellular Android Chrome) completes well before the T-8s
        // crossfade trigger. Added a SECONDARY window at T-15 so a
        // silent-fail primary doesn't have to wait until T-10 to retry.
        //
        // Why the backup: `m.playbackQueue.append({song})` is documented as
        // returning a promise that resolves once the item is queued, but on
        // Android Chrome + Widevine we've observed it resolve without
        // actually adding to the queue when the DRM session is briefly
        // busy. We verify via queue.length delta, and if the length didn't
        // grow we retry in the next window — by then the DRM session is
        // idle and the append lands.
        if (autoDJRef.current && playingNow && dur > 30 && !queueLockRef.current) {
          const remaining = dur - cur;
          const curId = nowPlayingIdRef.current;
          const alreadyPrimed = curId && prewarmSucceededForIdRef.current === curId;
          const inPrimaryWindow   = remaining <= 25 && remaining > 23;
          const inSecondaryWindow = remaining <= 15 && remaining > 13;
          const inBackupWindow    = remaining <= 10 && remaining > 8;
          if (curId && !alreadyPrimed && (inPrimaryWindow || inSecondaryWindow || inBackupWindow)) {
            const windowLabel = inPrimaryWindow
              ? 'primary T-25'
              : (inSecondaryWindow ? 'secondary T-15' : 'backup T-10');
            (async () => {
              try {
                const qRes = await axios.get(`${API}/queue/current`);
                const nt = qRes.data?.next_track;
                if (!nt) return;
                const nextId = looksLikeAppleId(nt.id) ? nt.id : (nt.apple_id || await resolveAppleId(nt).catch(() => null));
                if (!nextId) return;
                // (1) catalog GET — warms Apple CDN
                if (m.api?.song) {
                  await m.api.song(nextId).catch(() => null);
                } else if (m.api?.v1?.catalog?.song) {
                  await m.api.v1.catalog.song(nextId).catch(() => null);
                }
                // (2) queue.append — capture queue length + items BEFORE so
                // we can verify the append actually landed. MusicKit v3
                // exposes the queue as `m.playbackQueue` on modern builds
                // and `m.queue` on older; the length may live at `.length`
                // or `.items.length`. The old code read only `m.queue?.length`
                // and MISSED every successful append on MusicKit v3 tablets
                // — production RCA (Feb 2026) showed the arm endpoint was
                // never called in 24h of traffic. This robust probe checks
                // BOTH surfaces + verifies by ID presence, which is the
                // signal that actually matters.
                const probeQueueState = () => {
                  const pbq = m.playbackQueue || m.queue || {};
                  const items = Array.isArray(pbq.items) ? pbq.items : [];
                  const len = typeof pbq.length === 'number' ? pbq.length : items.length;
                  const ids = items.map((it) => String(it?.id || it?.container?.id || ''));
                  return { len, ids };
                };
                const pre = probeQueueState();
                let appendedOk = false;
                try {
                  if (m.playbackQueue && typeof m.playbackQueue.append === 'function') {
                    await m.playbackQueue.append({ song: nextId });
                  } else if (m.queue && typeof m.queue.append === 'function') {
                    await m.queue.append({ song: nextId });
                  }
                  // Give MusicKit a tick to reflect the append in its own
                  // queue snapshot — some Android Chrome builds delay the
                  // mutation by one microtask.
                  await new Promise((r) => setTimeout(r, 50));
                  const post = probeQueueState();
                  // Primary signal: the appended id is now in the queue.
                  const idPresent = post.ids.includes(String(nextId));
                  // Secondary signal: overall length grew (guards against
                  // MusicKit builds that don't populate `items` synchronously).
                  const lengthGrew = post.len > pre.len;
                  if (idPresent || lengthGrew) {
                    appendedOk = true;
                    console.info(
                      `[Prewarm] queue.append verified idPresent=${idPresent} length=${pre.len}→${post.len} for ${nextId} (${windowLabel})`,
                    );
                  } else {
                    const nextWindow = inPrimaryWindow
                      ? ' — will retry at secondary T-15'
                      : (inSecondaryWindow ? ' — will retry at backup T-10' : '');
                    console.warn(
                      `[Prewarm] queue.append silent-fail — id NOT in queue AND length unchanged (${pre.len}→${post.len}) (${windowLabel})${nextWindow}`,
                    );
                  }
                } catch (appendErr) {
                  console.warn(`[Prewarm] queue.append threw (${windowLabel}) —`, appendErr?.message);
                }
                // (3) Feb 2026 fix (production RCA): FIRE THE ARM UNCONDITIONALLY.
                //
                // Original design gated the arm endpoint on a verified queue.append
                // (`if (appendedOk)`). Two problems:
                //   1. MusicKit v3's playbackQueue.append is unreliable on prod
                //      tablets — production evidence showed the arm endpoint
                //      fired 0 times in 24h (RCA 86a0542b, Feb 2026), meaning
                //      every transition fell through to the cold path.
                //   2. The arm's real purpose is a *backend mutex* (see the
                //      /queue/prewarm docstring): tells the server "next
                //      track is X, so late guest injects skip past this
                //      slot." That's valuable even when we can't verify the
                //      client-side append.
                //
                // New behaviour: after a successful catalog GET (which warms
                // Apple's CDN, the biggest latency win), fire the arm
                // regardless of append outcome. If append DID succeed, the
                // fast-path stays intact. If it didn't, the backend still
                // knows what's coming and can protect it from displacement.
                if (curId) {
                  prewarmSucceededForIdRef.current = curId;
                  try {
                    await axios.post(`${API}/queue/prewarm`, { next_track_id: String(nt.id) });
                    if (!appendedOk) {
                      console.info(
                        `[Prewarm] arm fired despite unverified append — Apple catalog cache warmed for ${nextId} (${windowLabel})`,
                      );
                    }
                  } catch (lockErr) {
                    console.warn('[Prewarm] backend lock failed —', lockErr?.message);
                  }
                }
              } catch (_) { /* noop */ }
            })();
          }
        }

        // ===== Auto-crossfade trigger @ T-8s =====
        // Fires 8s before natural song end so the choreographed curve
        // (fade-out T-8→T-5, hand-off @ T-5, fade-in T-5→T-3) has room to
        // land its incoming ramp before the outgoing would have naturally
        // ended. Guarded by `crossfadeTriggeredForIdRef` for at-most-once
        // per song, plus queueLockRef + fadeIntervalRef so a manual
        // Next/Prev can't collide with an in-flight fade.
        if (autoDJRef.current && playingNow && dur > 20 && !queueLockRef.current && !fadeIntervalRef.current) {
          const remaining = dur - cur;
          const curId = nowPlayingIdRef.current;
          if (remaining <= FADE_TRIGGER_SEC && remaining > 0 && curId && crossfadeTriggeredForIdRef.current !== curId) {
            crossfadeTriggeredForIdRef.current = curId;
            startCrossfadeRef.current();
          }
        }

        // === Playhead-stall detector (silent-tail early cut-over) ===
        // When a track's audible content ends but MusicKit's metadata
        // duration includes trailing silence padding, we see `cur` freeze
        // while `playbackState` remains 2 (playing). Firing the crossfade
        // on this signal skips the dead air the guests would otherwise
        // hear. Guarded so it can't fire during the crossfade itself
        // (queueLockRef) or during normal buffering pauses at song start
        // (require cur > 30 to only apply near end-of-track).
        if (
          autoDJRef.current && playingNow && dur > 30 && !queueLockRef.current && !fadeIntervalRef.current
        ) {
          const curId = nowPlayingIdRef.current;
          const curFrozen = Math.abs(cur - lastPlayheadCurRef.current) < 0.05; // <50ms movement per 500ms tick = stalled
          if (curFrozen && cur > 30) {
            stalledTicksRef.current += 1;
          } else {
            stalledTicksRef.current = 0;
          }
          // 4 consecutive stalled ticks = 2 seconds of no playhead
          // advancement. Solid signal that the audible track has ended.
          if (
            stalledTicksRef.current >= 4 &&
            curId &&
            stallCrossfadeFiredForIdRef.current !== curId &&
            crossfadeTriggeredForIdRef.current !== curId
          ) {
            stallCrossfadeFiredForIdRef.current = curId;
            crossfadeTriggeredForIdRef.current = curId;
            stalledTicksRef.current = 0;
            console.info('[StallDetect] silent tail detected — cur=', cur.toFixed(2),
              'dur=', dur.toFixed(2),
              'gap=', (dur - cur).toFixed(2), 's — firing early crossfade');
            startCrossfadeRef.current();
          }
        }
        lastPlayheadCurRef.current = cur;

        // Intelligent Wash — no longer fires from the polling tick.
        // Moved INTO startCrossfade() (see comment there) so the wash is
        // deterministically fired at T-8s regardless of tick alignment or
        // MusicKit nowPlayingItem flicker. Leaving this sentinel comment
        // so future readers don't add a duplicate trigger.
      } catch (e) { /* noop */ }
    };
    const interval = createBackgroundInterval(tick, 500);
    return () => { interval.clear(); };
  }, [playerReady, slotATrack, slotBTrack]);

  // === Media Session API ===
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    if (!currentTrack) {
      try { navigator.mediaSession.metadata = null; } catch (e) { /* noop */ }
      return;
    }
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: currentTrack.name || 'Now playing',
        artist: (currentTrack.artists || []).map(a => typeof a === 'string' ? a : a?.name).filter(Boolean).join(', ') || 'MusicBox',
        album: 'MusicBox · DJ Console',
        artwork: currentTrack.album_image ? [
          { src: currentTrack.album_image, sizes: '512x512', type: 'image/jpeg' },
          { src: currentTrack.album_image, sizes: '256x256', type: 'image/jpeg' },
        ] : [],
      });
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    } catch (e) { /* noop */ }
  }, [currentTrack, isPlaying]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const setHandler = (action, cb) => {
      try { navigator.mediaSession.setActionHandler(action, cb); } catch (e) { /* noop */ }
    };
    setHandler('play', () => { play(); });
    setHandler('pause', () => { pause(); });
    setHandler('nexttrack', () => { handleTrackEndedRef.current?.(); });
    setHandler('previoustrack', () => { previousTrack(); });
    return () => {
      ['play','pause','nexttrack','previoustrack'].forEach(a => setHandler(a, null));
    };
  }, [play, pause, previousTrack]);

  // ===== Frontend cache-bust / version check (Feb 2026) =====
  // Mobile Safari and Chrome-on-Android serve stale JS after a deploy —
  // the "old JS + new backend" mismatch is what let the Feb 3 stall bug
  // hit multiple venues before the redeploy caught up. Countermeasure:
  // poll a backend timestamp that rotates on every boot (APP_VERSION_
  // STARTED_AT). When it differs from what we saw on this tab's own boot
  // for 2 consecutive polls, hard-reload WITH a cache-bust query string.
  // Reload is deferred until AFTER the current track ends so we never
  // interrupt live playback for a housekeeping refresh.
  //
  // Feb 4 update — false-positive spam. Two fixes:
  //   (1) Backend `APP_VERSION` is now bundle-hash based, stable across
  //       pod restarts (see server.py `_compute_stable_app_version`).
  //   (2) Frontend now requires the new version to be seen `N` times
  //       AND the last `N` polls to have never returned our own version.
  //       This makes a load-balancer flip (pod A → pod B → pod A) impossible
  //       to trip a reload — the moment we see `mine` again, we reset.
  //       Also bumped the streak from 2 → 3.
  const VERSION_MISMATCH_STREAK_TO_RELOAD = 5;
  // Oct 3 2026 incident guard — two prod pods reported different versions
  // and every client reloaded at each song end. Remember recent reloads so a
  // version we've already flipped to/from can never trigger another reload
  // within 12h, and never reload more than once per 6h for any reason.
  const VERSION_RELOAD_LS_KEY = 'spin.versionReloads';
  const VERSION_RELOAD_MIN_GAP_MS = 6 * 60 * 60 * 1000;
  const VERSION_RELOAD_MEMORY_MS = 12 * 60 * 60 * 1000;
  const readVersionReloads = () => {
    try {
      const raw = JSON.parse(window.localStorage.getItem(VERSION_RELOAD_LS_KEY) || '[]');
      const cutoff = Date.now() - VERSION_RELOAD_MEMORY_MS;
      return Array.isArray(raw) ? raw.filter((r) => r && r.at > cutoff) : [];
    } catch { return []; }
  };
  const versionReloadAllowed = (from, to) => {
    const recent = readVersionReloads();
    const newest = recent.reduce((m, r) => Math.max(m, r.at), 0);
    if (Date.now() - newest < VERSION_RELOAD_MIN_GAP_MS) return 'too_soon';
    if (recent.some((r) => r.to === to || r.from === to)) return 'known_version_flip';
    return null;
  };
  const rememberVersionReload = (from, to) => {
    try {
      window.localStorage.setItem(VERSION_RELOAD_LS_KEY, JSON.stringify([...readVersionReloads(), { from, to, at: Date.now() }]));
    } catch { /* noop */ }
  };
  const versionRef = useRef({ mine: null, lastSeen: null, mismatchCount: 0, pendingReload: false, suppressedFor: null });
  useEffect(() => {
    let disposed = false;
    const forceReload = () => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('v', String(Date.now()));
        window.location.replace(url.toString());
      } catch {
        // Any URL parsing failure → fall back to a plain reload.
        window.location.reload();
      }
    };
    const check = async () => {
      if (disposed) return;
      try {
        const r = await axios.get(`${API}/system/version`);
        const server = r.data?.version;
        if (!server) return;
        const state = versionRef.current;
        if (state.mine == null) {
          state.mine = server;
          return;
        }
        if (server === state.mine) {
          // Load-balancer flipped back to a pod running our bundle → we
          // are definitively NOT stale. Reset any accumulated mismatch.
          state.mismatchCount = 0;
          state.lastSeen = null;
          return;
        }
        // Same NEW version seen `VERSION_MISMATCH_STREAK_TO_RELOAD` times
        // in a row (with no `mine` sighting in between)? Only then trust
        // the mismatch — avoids reloading on transient backend restarts,
        // rolling deploys, or pod hopping.
        if (server === state.lastSeen) {
          state.mismatchCount += 1;
        } else {
          state.mismatchCount = 1;
        }
        state.lastSeen = server;
        if (state.mismatchCount >= VERSION_MISMATCH_STREAK_TO_RELOAD && !state.pendingReload) {
          const blocked = versionReloadAllowed(state.mine, server);
          if (blocked) {
            if (state.suppressedFor !== server) {
              state.suppressedFor = server;
              console.warn('[version-check] Reload suppressed (', blocked, ') mine=', state.mine, 'server=', server);
              reportPlaybackStall('version_reload_suppressed', { reason: blocked, mine: state.mine, server });
            }
            return;
          }
          state.pendingReload = true;
          state.forceReload = () => { rememberVersionReload(state.mine, server); forceReload(); };
          console.info('[version-check] Stale bundle detected — will reload after current track ends.');
          try { toast.info('New version available — refreshing after this song.'); } catch { /* noop */ }
          // If nothing is playing right now, reload immediately.
          if (!musicRef.current || musicRef.current.playbackState !== 2) state.forceReload();
        }
      } catch { /* noop — version check must never throw */ }
    };
    const iv = setInterval(check, 60_000);
    check();
    return () => { disposed = true; clearInterval(iv); };
  }, []);

  // Trigger the deferred reload the moment playback actually stops.
  useEffect(() => {
    const state = versionRef.current;
    if (!state.pendingReload || !state.forceReload) return;
    if (isPlaying) return;
    // Small delay so the "song finished" audio actually decays first.
    const t = setTimeout(() => state.forceReload(), 1500);
    return () => clearTimeout(t);
  }, [isPlaying]);

  // ===== Feb 2026 (Bangalore stall RCA `64ce139d`) — time-series playback
  // transition telemetry =====
  // Server DB previously stored only a single-snapshot playback_state doc
  // per host, so pause patterns (BT contention, OEM battery-saver, code
  // fault) were undiagnosable. Every isPlaying flip now writes a row to
  // `playback_transitions` with the full state — MusicKit playback state
  // number, `playIntentRef`, `document.hidden`, current track, UA.
  // Rate-limited to 1/second so a rapidly-toggling MusicKit state (WAITING
  // ↔ PLAYING during buffering) can't hammer the collection.
  const lastTransitionAtRef = useRef(0);
  const prevIsPlayingRef = useRef(isPlaying);
  const prevMusicKitStateRef = useRef(null);
  useEffect(() => {
    const prev = prevIsPlayingRef.current;
    prevIsPlayingRef.current = isPlaying;
    if (prev === isPlaying) return;
    const now = Date.now();
    if (now - lastTransitionAtRef.current < 1000) return;
    lastTransitionAtRef.current = now;
    try {
      const m = musicRef.current;
      const currentState = m?.playbackState;
      const payload = {
        prev_musickit_state: prevMusicKitStateRef.current,
        new_musickit_state: typeof currentState === 'number' ? currentState : null,
        play_intent: !!playIntentRef.current,
        doc_hidden: typeof document !== 'undefined' ? !!document.hidden : false,
        current_track_id: String(currentTrackRef.current?.id || ''),
        current_track_name: String(currentTrackRef.current?.name || ''),
        user_agent: (typeof navigator !== 'undefined' && navigator.userAgent) || '',
        is_authorized: !!isAuthorized,
        at: new Date().toISOString(),
      };
      prevMusicKitStateRef.current = payload.new_musickit_state;
      axios.post(`${API}/telemetry/playback-transition`, payload).catch(() => { /* noop */ });
    } catch { /* noop */ }
  }, [isPlaying, isAuthorized]);

  // === isPlaying-driven keepalive ===
  useEffect(() => {
    if (!isPlaying) stopKeepAlive();
  }, [isPlaying]);

  // === Silent audio anchor — keeps the tab marked "audible" so Chrome
  // (especially on Mac) won't throttle/suspend us during the brief silent
  // gaps between songs. See gen_silent_anchor.py for the why.
  useEffect(() => {
    if (isPlaying) startSilentAnchor();
    else stopSilentAnchor();
    // Cleanup on unmount
    return () => stopSilentAnchor();
  }, [isPlaying, startSilentAnchor, stopSilentAnchor]);

  // === Auto-resume on screen unlock / tab refocus + long-idle rehydrate ==
  // Mobile Chrome suspends the audio context when the screen locks and the
  // tab is backgrounded for too long; on unlock the audio remains paused
  // until a user gesture. We listen for `visibilitychange` (tab returns to
  // foreground) — that user-gesture-equivalent moment lets us safely call
  // play() again so the host doesn't have to manually tap resume.
  //
  // Feb 12 2026 hardening (Yazu Bangalore forensics — 31 ×
  // `watchdog_foreground_recover` in 5h):
  //   1. Track when the tab went hidden. If we return after >5 minutes,
  //      silently re-hydrate the backend queue and fire a
  //      `long_idle_wake_rehydrate` telemetry — an event that long implies
  //      Android has torn down our WebSocket / HTTP keep-alives, and we
  //      should sync state before resuming rather than just calling play().
  //   2. Verify play() actually produced audio (position advances by
  //      >0.3s within 2s). If not, escalate to handleTrackEnded → and
  //      if STILL frozen, emergency hard-reset MusicKit. Previously we
  //      just called play() and hoped — that's exactly the failure the
  //      31 recoveries were papering over.
  const hiddenSinceRef = useRef(null);
  useEffect(() => {
    const LONG_IDLE_MS = 5 * 60 * 1000; // 5 minutes
    const onVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') {
        // Tab went to background — start the idle clock.
        if (!hiddenSinceRef.current) hiddenSinceRef.current = Date.now();
        return;
      }
      // Tab returned to foreground.
      const hiddenSince = hiddenSinceRef.current;
      const hiddenFor = hiddenSince ? Date.now() - hiddenSince : 0;
      hiddenSinceRef.current = null;
      const m = musicRef.current;
      if (!m) return;
      if (!playIntentRef.current) return;

      // 1) Long-idle rehydrate — the tab was hidden long enough that
      // Android likely killed our timers/WS. Sync backend state BEFORE
      // trying to resume so we don't call play() on a stale playlist.
      if (hiddenFor >= LONG_IDLE_MS) {
        reportPlaybackStall('long_idle_wake_rehydrate', {
          hidden_for_s: Math.round(hiddenFor / 1000),
          playback_state: m.playbackState,
        });
        console.info('[MusicContext] Long-idle wake (', Math.round(hiddenFor / 1000), 's ) — rehydrating queue.');
        try { await refreshQueue(); } catch { /* noop */ }
      }

      // 2) Hardened foreground recovery: call play() then verify the
      // playhead advances. If it doesn't within 2s, MusicKit is wedged
      // and just calling play() is a lie — escalate.
      if (m.playbackState !== 2) {
        try { await m.play?.(); } catch { /* noop */ }
      }
      // Grab position now, sample 2s later. If unchanged (or state stays
      // paused), escalate through handleTrackEnded → emergency hard-reset.
      let posNow = null;
      try { posNow = Number(m.currentPlaybackTime); } catch { posNow = null; }
      setTimeout(async () => {
        const m2 = musicRef.current;
        if (!m2 || !playIntentRef.current) return;
        // If we drifted into a queue rebuild or crossfade meanwhile, skip.
        if (queueLockRef.current || fadeIntervalRef.current) return;
        let posLater = null;
        try { posLater = Number(m2.currentPlaybackTime); } catch { posLater = null; }
        const stillPaused = m2.playbackState !== 2;
        const posFrozen = posNow != null && posLater != null && Number.isFinite(posNow)
          && Number.isFinite(posLater) && (posLater - posNow) < 0.3;
        if (!stillPaused && !posFrozen) return; // healthy — audio is flowing
        console.warn('[MusicContext] Foreground health-check failed —',
                     'stillPaused=', stillPaused, 'posFrozen=', posFrozen,
                     '(', posNow, '→', posLater, '). Escalating.');
        reportPlaybackStall('foreground_health_check_failed', {
          hidden_for_s: Math.round(hiddenFor / 1000),
          still_paused: stillPaused,
          pos_frozen: posFrozen,
          pos_before: posNow,
          pos_after: posLater,
          playback_state: m2.playbackState,
        });
        try {
          handleTrackEndedRef.current?.();
        } catch { /* noop */ }
        // Give handleTrackEnded 3s to rebuild + start playing. If STILL
        // frozen, invoke the nuclear option — throttled to once per
        // 3min inside emergencyHardRecoverMusicKit itself.
        setTimeout(() => {
          const m3 = musicRef.current;
          if (!m3 || !playIntentRef.current) return;
          if (m3.playbackState === 2) return; // recovered
          emergencyHardRecoverRef.current?.('foreground_health_check_failed');
        }, 3000);
      }, 2000);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    // Fire once on mount to initialize hiddenSinceRef if we're already hidden.
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      hiddenSinceRef.current = Date.now();
    }
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [refreshQueue]);

  // ===== Feb 2026 (production RCA `b2721f85`) — auto-resume mid-song pause ====
  // Client Nishant reported "pauses on its own" AFTER the auth-guardrail
  // shipped. Deployer RCA showed playback_state pushes `is_playing:false`
  // with a fresh updated_at while the heartbeat keeps working — no crash,
  // no auth failure, no queue stall. Root cause: OS/browser audio-focus
  // loss (notification, another app briefly plays, Android battery saver).
  //
  // The existing visibilitychange handler only fires when the tab goes
  // hidden→visible. This handler ALSO catches the case where the tab stays
  // visible but audio focus was still yanked (WhatsApp notification with
  // sound, incoming call ringtone even if declined, YouTube in another tab).
  //
  // Rule: if isPlaying flips true→false AND playIntentRef is still true,
  // it's an OS interruption (our `pause()` callback sets playIntent=false
  // first). Retry play() indefinitely with a short delay — DON'T give up
  // after 3 attempts.
  //
  // Feb 8 2026 update — production forensics showed 25 mid_song_pause
  // reports over 48h at bengaluru.yazu@gmail.com. Each report meant a
  // 3-attempt burst gave up and told the DJ to tap play. That's the
  // "annoying stop after a few songs" experience. Fix:
  //   • First attempt IMMEDIATE (0ms), then 300ms, 700ms — total ~1s
  //     to the first three attempts (was 2.4s).
  //   • DON'T give up: after the burst, keep retrying every 5s for
  //     up to 60s while playIntent stays true. Only surface the toast
  //     if we still can't recover after 12 attempts (~60s).
  //   • Report telemetry once per pause event (not per attempt), so
  //     Forensics still sees the incident but isn't flooded.
  //
  // Sep 2026 consolidation — this used to run ALONGSIDE a second, older
  // resume loop inline in `onState`'s PAUSED (state===3) branch above.
  // Both fired off the same real pause, with two different telemetry
  // reasons and two uncoordinated retry loops. This effect is now the
  // SOLE recovery path for "stopped playing while we intended to keep
  // playing" — it inherits the older path's guard against firing during
  // a legitimate in-flight queue rebuild / crossfade (a normal transient
  // isPlaying=false during those isn't a real stall).
  const lastResumeAtRef = useRef(0);
  useEffect(() => {
    // Only react when transitioning to NOT-playing while user intended to play.
    if (isPlaying) return;
    if (!playIntentRef.current) return;
    // Inherited from the retired `onState` inline resume — don't fight a
    // queue rebuild or crossfade already in progress; those flip isPlaying
    // false/true transiently as part of normal operation, not a stall.
    if (queueLockRef.current || fadeIntervalRef.current) return;
    const m = musicRef.current;
    if (!m) return;
    // Debounce — don't chain-resume if we're already trying in a fresh window.
    const now = Date.now();
    if (now - lastResumeAtRef.current < 2000) return;
    lastResumeAtRef.current = now;
    let cancelled = false;
    const reportedRef = { current: false };
    const tryResume = async (attempt) => {
      if (cancelled) return;
      // Attempt schedule: 0, 300ms, 700ms, then every 5s.
      const delay = attempt === 1 ? 0
                  : attempt === 2 ? 300
                  : attempt === 3 ? 700
                  : 5000;
      await new Promise((r) => setTimeout(r, delay));
      if (cancelled) return;
      if (!playIntentRef.current) return; // user paused legitimately
      const m2 = musicRef.current;
      if (!m2) return;
      if (m2.playbackState === 2) {
        if (attempt > 1) console.info(`[auto-resume] recovered on attempt ${attempt}`);
        return; // recovered
      }
      try {
        await m2.play?.();
        // Also poke the silent audio anchor — it holds the OS's audio
        // focus for us; if MusicKit briefly lost focus, replaying the
        // anchor helps reclaim it so the next m.play() actually pushes
        // audio through instead of the SDK claiming "playing" silently.
        try { setSilentAudioPlaying(true); } catch { /* noop */ }
      } catch (err) {
        // MusicKit sometimes throws "already playing" or "queue empty" —
        // both benign here. Continue the loop.
      }
      // Verify — if still not playing, escalate to handleTrackEnded on
      // attempt 4 (queue rebuild), otherwise keep looping.
      await new Promise((r) => setTimeout(r, 400));
      if (cancelled) return;
      const m3 = musicRef.current;
      if (m3 && m3.playbackState === 2) {
        if (attempt > 1) console.info(`[auto-resume] recovered on attempt ${attempt}`);
        return;
      }
      // Attempt 4: one queue-rebuild attempt. Often unblocks a wedged SDK.
      if (attempt === 4) {
        try {
          console.warn('[auto-resume] attempt 4 — escalating to handleTrackEnded (queue rebuild)');
          handleTrackEndedRef.current?.();
        } catch (_) { /* noop */ }
        await new Promise((r) => setTimeout(r, 1500));
        if (cancelled) return;
        if (musicRef.current?.playbackState === 2) return;
      }
      // Report exactly once per pause incident — so Forensics sees the
      // event even if it eventually recovers. Carries the richer
      // now-playing context the retired `onState` inline report used to
      // send, so consolidating the two paths doesn't lose diagnostic detail.
      if (!reportedRef.current && attempt >= 3) {
        reportedRef.current = true;
        reportPlaybackStall('unexpected_mid_song_pause', {
          attempt_when_reported: attempt,
          playback_state: m3?.playbackState,
          now_playing_id: nowPlayingIdRef.current,
          cur: (() => { try { return m3?.currentPlaybackTime; } catch { return null; } })(),
          dur: (() => { try { return m3?.currentPlaybackDuration; } catch { return null; } })(),
          is_hidden: typeof document !== 'undefined' ? document.hidden : null,
        });
      }
      // Give up after ~60s (attempts: 0, 0.3s, 0.7s, then every 5s → ~12
      // more attempts). At that point the SDK is truly wedged and we
      // want a manual DJ intervention.
      if (attempt >= 15) {
        console.warn('[auto-resume] giving up after 15 attempts (~60s)');
        try {
          toast('Playback interrupted — tap the play button to resume.', {
            id: 'auto-resume-failed',
            duration: 8000,
          });
        } catch { /* noop */ }
        return;
      }
      tryResume(attempt + 1);
    };
    tryResume(1);
    return () => { cancelled = true; };
  }, [isPlaying]);

  // === Playback chain watchdog (long-session stall recovery) ===
  // Every crossfade rebuilds Apple's queue to a SINGLE track (so the backend
  // queue, not Apple's internal queue, is always the source of truth — see
  // the desync fixes from 2026-06-19). The chain depends on TWO upstream
  // events firing reliably forever:
  //   1. MusicKit's `playbackStateDidChange` → ENDED event, which our
  //      handler routes into `handleTrackEnded` to load the next track.
  //   2. The progress polling tick, which triggers `startCrossfade` at T-10s.
  // On long sessions (50+ minutes, ~15 tracks), MusicKit occasionally STOPS
  // firing the ENDED event when its queue is exhausted — playback simply
  // stalls in idle state and our chain has no way to advance. This watchdog
  // detects that stall and force-recovers by invoking handleTrackEnded.
  //
  // Triggers when ALL of the following are true:
  //   - host actively intends to play (`playIntentRef.current === true`)
  //   - autoDJ is on
  //   - we have a non-empty backend queue (something SHOULD be playing)
  //   - MusicKit reports playbackState !== 2 (not playing)
  //   - no crossfade in progress (`!fadeIntervalRef.current`)
  //   - no queue operation in progress (`!queueLockRef.current`)
  //   - condition has held continuously for 12+ seconds
  useEffect(() => {
    let stalledSince = 0;
    let recoveryAttempts = 0;   // count of handleTrackEnded fires without resuming
    let lastRecoveryAt = 0;
    let lastHardResetAt = 0;
    // Feb 12 2026 — hard-reset repeat escalation. Yazu Bangalore forensics
    // showed 13 × `hard_reset_reload_queue` in 5h. That means even the
    // queue-re-arm-on-same-MusicKit-instance path is silently failing:
    // MusicKit reports playbackState=2 after playTracks but the wedged
    // <audio> element continues emitting nothing. Track how many
    // hardResets fired in the last 5min — if 2+, escalate to nuclear
    // hardResetMusicKit which rebuilds the entire SDK singleton.
    let hardResetsInWindow = [];
    const HARD_RESET_ESCALATE_AFTER = 2;
    const HARD_RESET_ESCALATE_WINDOW_MS = 5 * 60 * 1000;
    const RECOVERY_THRESHOLD_MS = 12_000;
    // After N soft recoveries fail, fall back to a full queue re-arm.
    // This is what the field-report calls "auto-reselect the playlist"
    // — silently fetch the backend queue and call playTracks from the
    // saved current_index, which resets MusicKit's SDK state and often
    // fixes wedged tabs where handleTrackEnded alone couldn't.
    const HARD_RESET_AFTER_ATTEMPTS = 2;
    const HARD_RESET_COOLDOWN_MS = 90_000;
    const hardReset = async () => {
      const now = Date.now();
      if (now - lastHardResetAt < HARD_RESET_COOLDOWN_MS) return;
      // Feb 13 2026 — serialize with handleTrackEnded + emergencyRecovery.
      // Was previously the racer that caused the double-advance loop.
      if (!acquireRecovery('hardReset')) return;
      lastHardResetAt = now;
      try {
      // Track fire times, drop entries older than the window.
      hardResetsInWindow = hardResetsInWindow.filter(
        (t) => now - t < HARD_RESET_ESCALATE_WINDOW_MS,
      );
      hardResetsInWindow.push(now);
      // Repeat escalation — same-instance re-arm is proven ineffective.
      if (hardResetsInWindow.length >= HARD_RESET_ESCALATE_AFTER) {
        console.warn('[MusicContext] hardReset — REPEAT ×', hardResetsInWindow.length,
                     'in 5min — escalating to nuclear MusicKit rebuild.');
        hardResetsInWindow = [];
        recoveryAttempts = 0;
        stalledSince = 0;
        // Release the mutex FIRST — emergencyHardRecover will re-acquire.
        releaseRecovery();
        emergencyHardRecoverRef.current?.('hard_reset_repeat');
        return;
      }
      try {
        // Feb 13 2026 — advance-vs-replay decision must NOT depend only
        // on the fail counter (which is only incremented in the silent-
        // audio handoff branch, not in the throw-CONTENT_RESTRICTED
        // branch, nor in the missed-ENDED-event branch). Instead:
        //   - Peek the current track's playhead. If it's at or near
        //     the end (last 3s) OR at exactly 0 (MusicKit resets
        //     currentPlaybackTime to 0 when a track ends without a
        //     handoff), the current track is DONE — advance.
        //   - If playhead is mid-track (>3s from end AND >1s from start),
        //     playback froze mid-song — replay from current index so
        //     the DJ hears the song they were listening to.
        const failInfo = consecutiveHandoffFailsRef.current || { count: 0, appleId: null };
        let idxAdjustReason = null;
        let shouldAdvance = false;
        try {
          const m = musicRef.current;
          const pos = Number(m?.currentPlaybackTime ?? NaN);
          const dur = Number(m?.currentPlaybackDuration ?? NaN);
          const nearEnd = Number.isFinite(pos) && Number.isFinite(dur) && dur > 0 && (dur - pos) < 3;
          const atZero = Number.isFinite(pos) && pos < 0.5;
          shouldAdvance = nearEnd || atZero;
        } catch { /* noop */ }
        // Legacy pathway: if we KNOW the state watchdog was triggered by
        // counted handoff failures, always advance (the old path).
        if ((failInfo.count >= 1 && !isNetworkFlaky()) || shouldAdvance) {
          try {
            const adv = await axios.post(`${API}/queue/next`);
            if (adv.data?.current_index != null) {
              idxAdjustReason = failInfo.count >= 1
                ? `skip-past-broken-handoff (${failInfo.appleId})`
                : 'track-ended-advance';
              reportPlaybackStall('hard_reset_skip_broken_next', {
                broken_apple_id: failInfo.appleId || null,
                fail_count: failInfo.count,
                new_index: adv.data.current_index,
                advance_reason: idxAdjustReason,
              });
            }
          } catch (err) {
            console.warn('[MusicContext] hardReset skip-broken advance failed:', err?.message);
          }
          consecutiveHandoffFailsRef.current = { appleId: null, count: 0 };
        }
        const q = await axios.get(`${API}/queue/current`);
        const tracksNow = q.data?.tracks || [];
        const idx = Math.max(0, Math.min(
          q.data?.current_index || 0,
          tracksNow.length - 1,
        ));
        if (!tracksNow.length) {
          console.warn('[MusicContext] hardReset — backend queue empty, cannot recover');
          return;
        }
        console.warn('[MusicContext] hardReset — re-arming queue from backend @ index',
                     idx, '/', tracksNow.length, 'tracks',
                     idxAdjustReason ? `[${idxAdjustReason}]` : '');
        reportPlaybackStall('hard_reset_reload_queue', {
          idx, total: tracksNow.length, attempts: recoveryAttempts,
          skipped_broken: !!idxAdjustReason,
        });
        recoveryAttempts = 0;
        stalledSince = 0;
        await playTracksRef.current?.(tracksNow, idx, {
          playlistId: q.data?.playlist_id || null,
          playlistType: q.data?.playlist_type || null,
        });
      } catch (err) {
        console.warn('[MusicContext] hardReset failed:', err?.message);
      }
      } finally {
        releaseRecovery();
      }
    };
    // Hidden-tab safety net — the primary path prefers letting the
    // visibility handler recover, but on Android Chrome the
    // `visibilitychange` event doesn't fire when the SCREEN turns off
    // (only when the tab is switched), so a phone in a pocket can sit
    // silently for minutes. If we've been stalled AND hidden this long,
    // recover regardless. 30s is enough to not fight a legitimate short
    // audio-focus loss (call, notification) while still catching the
    // Android-screen-off failure mode.
    const HIDDEN_RECOVERY_THRESHOLD_MS = 30_000;
    let hiddenStalledSince = 0;
    const tick = () => {
      // Bail if obvious "we don't care" cases
      if (!playIntentRef.current)      { stalledSince = 0; hiddenStalledSince = 0; recoveryAttempts = 0; return; }
      if (!autoDJRef.current)           { stalledSince = 0; hiddenStalledSince = 0; recoveryAttempts = 0; return; }
      if (queueLockRef.current)         { stalledSince = 0; hiddenStalledSince = 0; return; }
      if (fadeIntervalRef.current)      { stalledSince = 0; hiddenStalledSince = 0; return; }
      const m = musicRef.current;
      if (!m)                           { stalledSince = 0; hiddenStalledSince = 0; return; }
      // Already playing — chain is healthy, reset attempt counter.
      if (m.playbackState === 2) {
        stalledSince = 0; hiddenStalledSince = 0; recoveryAttempts = 0;
        // Oct 3 2026 — late-success reconciliation: a handoff we gave up on
        // is now audibly playing. Advance the backend pointer ONCE so the
        // track is not served again as "next" when it ends.
        const pending = pendingHandoffRef.current;
        if (pending && String(m.nowPlayingItem?.id || '') === pending.appleId) {
          pendingHandoffRef.current = null;
          reportPlaybackStall('handoff_late_success_reconciled', {
            apple_id: pending.appleId, current_index: pending.nextIndex, via: 'watchdog',
            late_by_s: Math.round((Date.now() - pending.at) / 1000),
          });
          axios.post(`${API}/queue/next`).then(() => refreshQueueRef.current?.()).catch(() => {});
        }
        return;
      }
      // Oct 3 2026 — if the SDK is still loading/buffering (1/8) give it
      // 25 s before we yank the queue; a slow DRM fetch is not a stall.
      const threshold = (m.playbackState === 1 || m.playbackState === 8) ? 25_000 : RECOVERY_THRESHOLD_MS;
      const now = Date.now();
      const isHidden = typeof document !== 'undefined' && document.hidden;
      if (isHidden) {
        // Track a SEPARATE hidden-only stall window so the 30s escape
        // hatch can trip even without a foreground period.
        if (!hiddenStalledSince) hiddenStalledSince = now;
        if (now - hiddenStalledSince >= HIDDEN_RECOVERY_THRESHOLD_MS) {
          console.warn('[MusicContext] Playback watchdog — hidden-tab escape hatch after',
                       Math.round((now - hiddenStalledSince) / 1000), 's. Recovering.');
          reportPlaybackStall('watchdog_hidden_recover', {
            elapsed_s: Math.round((now - hiddenStalledSince) / 1000),
            playback_state: musicRef.current?.playbackState,
            attempts_so_far: recoveryAttempts,
          });
          hiddenStalledSince = 0;
          stalledSince = 0;
          recoveryAttempts += 1;
          lastRecoveryAt = now;
          handleTrackEndedRef.current?.();
          if (recoveryAttempts >= HARD_RESET_AFTER_ATTEMPTS) hardReset();
          return;
        }
        // Under threshold, still hidden — defer to visibility handler.
        stalledSince = 0;
        return;
      }
      // Foreground stall window
      if (!stalledSince) stalledSince = now;
      hiddenStalledSince = 0;   // reset the hidden window when we come back
      if (now - stalledSince < threshold) return;
      // 12+ seconds of stall while we should be playing — force advance.
      console.warn('[MusicContext] Playback watchdog tripped — stalled for',
                   Math.round((now - stalledSince) / 1000), 's. Recovering via handleTrackEnded.');
      reportPlaybackStall('watchdog_foreground_recover', {
        elapsed_s: Math.round((now - stalledSince) / 1000),
        playback_state: musicRef.current?.playbackState,
        attempts_so_far: recoveryAttempts,
      });
      stalledSince = 0;
      recoveryAttempts += 1;
      lastRecoveryAt = now;
      // Feb 13 2026 — SEQUENCE, don't race.
      // Was previously: fire handleTrackEnded (unawaited) + call
      // hardReset() in the SAME tick. Both then concurrently POSTed
      // /queue/next + rebuilt MusicKit's queue → the loop bug.
      // Now: await handleTrackEnded. If we recover to playing state
      // within 3s, we're done. Otherwise escalate to hardReset AFTER
      // the previous recovery has fully released its mutex.
      (async () => {
        try {
          await handleTrackEndedRef.current?.();
        } catch (err) {
          console.warn('[MusicContext] watchdog handleTrackEnded threw:', err?.message);
        }
        if (recoveryAttempts < HARD_RESET_AFTER_ATTEMPTS) return;
        // Give MusicKit up to 3s to reach playing state (state===2)
        // after the handleTrackEnded rebuild before deciding to
        // escalate. Prevents hardReset firing on a queue rebuild
        // that just needs a moment to complete DRM handshake.
        const settled = await new Promise((resolve) => {
          let elapsed = 0;
          const iv = setInterval(() => {
            const m = musicRef.current;
            if (m && m.playbackState === 2) { clearInterval(iv); resolve(true); return; }
            elapsed += 100;
            if (elapsed >= 3000) { clearInterval(iv); resolve(false); }
          }, 100);
        });
        if (settled) return; // handleTrackEnded recovered us
        // Still not playing — hardReset() acquires its own mutex and
        // will bail out if another recovery has meanwhile started.
        hardReset();
      })();
    };
    // Use the Web-Worker-backed interval so Chrome's aggressive tab
    // throttling on Android (1/min for hidden tabs) can't gag the
    // watchdog. Without this, the client sees "stops after every song"
    // whenever the phone screen locks mid-set.
    const handle = createBackgroundInterval(tick, 3_000);
    return () => handle.clear();
  }, []);

  // === Silent-audio watchdog (Feb 6 2026) =================================
  // The playbackState watchdog above catches paused / ended / stopped
  // states while intent=play. It does NOT catch the failure mode the
  // client keeps reporting: MusicKit says PLAYING (state=2) but no
  // audio is coming out — currentPlaybackTime frozen for 10+ seconds.
  //
  // This is the classic "silent audio failure" on Android Chrome:
  //   - Audio graph disconnected without a state event
  //   - Apple DRM key silently expired mid-stream
  //   - AudioContext suspended by the OS
  //   - Media route change (BT headphones disconnect) not raised
  //
  // Detection: sample currentPlaybackTime every 2s. If state===2 AND
  // position hasn't advanced by at least 0.5s across two samples,
  // we're silently stalled. Recover via handleTrackEnded (which
  // rebuilds the queue) — same soft path the state watchdog uses.
  //
  // Cannot rely on setInterval — Android throttles it to 1/min in
  // hidden tabs. Web-Worker interval is essential.
  useEffect(() => {
    let lastPosition = null;
    let stalledAt = null;
    let lastRecoveryAt = 0;
    const RECOVER_COOLDOWN_MS = 15_000;
    const POSITION_STALL_MS = 8_000; // 4 samples in a row frozen

    // Feb 12 2026 — repeat-freeze escalation. If we frozen-recover 2+ times
    // on the same track within a 60s window, the soft path (seek+play +
    // handleTrackEnded queue rebuild) is proven insufficient — the audio
    // element itself is dead. Escalate to the nuclear hard-reset.
    let recoveriesThisTrack = 0;
    let recoveryTrackId = null;
    let firstRecoveryAt = 0;
    const REPEAT_WINDOW_MS = 60_000;
    const HARD_ESCALATE_AFTER = 2;

    const tick = () => {
      if (!playIntentRef.current)  { lastPosition = null; stalledAt = null; return; }
      if (queueLockRef.current)    { lastPosition = null; stalledAt = null; return; }
      if (fadeIntervalRef.current) { lastPosition = null; stalledAt = null; return; }
      const m = musicRef.current;
      if (!m || m.playbackState !== 2) {
        // Not in PLAYING state — the state watchdog owns this window.
        lastPosition = null; stalledAt = null; return;
      }
      let pos = null;
      try { pos = Number(m.currentPlaybackTime); } catch { pos = null; }
      if (pos == null || !Number.isFinite(pos)) return;

      // First sample after entering PLAYING — establish baseline.
      if (lastPosition == null) { lastPosition = pos; stalledAt = null; return; }

      // Position advanced by at least half a second → healthy playback.
      if (pos - lastPosition >= 0.5) {
        lastPosition = pos;
        stalledAt = null;
        // Reset the repeat-freeze counter if we've played healthily for
        // longer than the repeat window — the track is fine after all.
        if (recoveryTrackId && (Date.now() - firstRecoveryAt) > REPEAT_WINDOW_MS) {
          recoveriesThisTrack = 0;
          recoveryTrackId = null;
        }
        return;
      }

      // Position didn't advance. Start / continue the stall clock.
      const now = Date.now();
      if (!stalledAt) { stalledAt = now; return; }
      if (now - stalledAt < POSITION_STALL_MS) return;

      // 8+ seconds of frozen position while MusicKit claims playing.
      if (now - lastRecoveryAt < RECOVER_COOLDOWN_MS) return;
      lastRecoveryAt = now;
      const curId = nowPlayingIdRef.current || null;
      // Track repeat freezes across the same audio element.
      if (recoveryTrackId !== curId || (now - firstRecoveryAt) > REPEAT_WINDOW_MS) {
        recoveryTrackId = curId;
        recoveriesThisTrack = 0;
        firstRecoveryAt = now;
      }
      recoveriesThisTrack += 1;
      console.warn('[MusicContext] Silent-audio watchdog: currentPlaybackTime frozen at',
                   pos.toFixed(1), 's for', Math.round((now - stalledAt) / 1000),
                   's while playbackState=2. Recovering (attempt', recoveriesThisTrack, 'on this track).');
      reportPlaybackStall('silent_audio_position_frozen', {
        frozen_at_s: Number(pos.toFixed(1)),
        frozen_for_s: Math.round((now - stalledAt) / 1000),
        now_playing_id: curId,
        repeat_count_60s: recoveriesThisTrack,
        is_hidden: typeof document !== 'undefined' ? document.hidden : null,
      });
      stalledAt = null;
      lastPosition = null;
      // Two or more silent-freezes on the SAME track within 60s = the
      // MusicKit `<audio>` element is dead. Soft nudges won't save it.
      // Skip straight to the nuclear rebuild.
      if (recoveriesThisTrack >= HARD_ESCALATE_AFTER) {
        console.warn('[MusicContext] Silent-audio watchdog — REPEAT freeze, escalating to hard MusicKit reset.');
        recoveriesThisTrack = 0;
        recoveryTrackId = null;
        emergencyHardRecoverRef.current?.('silent_audio_repeat_freeze');
        return;
      }
      // First freeze — try the soft path (seek+play, then handleTrackEnded).
      try {
        const nudgeTo = Math.max(0, pos - 0.05);
        if (typeof m.seekToTime === 'function') m.seekToTime(nudgeTo);
        Promise.resolve(m.play?.()).catch(() => {});
      } catch (_) { /* noop */ }
      // If still not moving 3 s later, escalate to a full track-end
      // (which does queue rebuild + DRM refresh).
      setTimeout(() => {
        try {
          if (musicRef.current?.playbackState !== 2 && playIntentRef.current) {
            handleTrackEndedRef.current?.();
          }
        } catch (_) { /* noop */ }
      }, 3000);
    };

    const handle = createBackgroundInterval(tick, 2_000);
    return () => handle.clear();
  }, []);

  // === Stuck-lock watchdog (Feb 8 2026) ==================================
  // The T-8s crossfade trigger is guarded by `!queueLockRef.current` and
  // `!fadeIntervalRef.current`. Any code path that sets one of those
  // true and then fails to clear it in a `finally` (unhandled exception
  // in a deep async chain, refreshQueue rejection, DRM error mid-
  // rebuild) leaks the lock — every SUBSEQUENT song's crossfade is
  // silently blocked. Result: "one song ends, next one starts, no mix"
  // as reported by Yazu Bangalore.
  //
  // This watchdog snapshots the two refs every 5s. If the SAME ref has
  // been true across two consecutive snapshots (>=10s continuously),
  // force-clear it and log the leak. Real crossfades finish inside 5s
  // so a healthy lock is never held that long.
  useEffect(() => {
    let lastQueueLockTrueAt = 0;
    let lastFadeIntervalTrueAt = 0;
    const STUCK_THRESHOLD_MS = 30_000; // 30s == way past any legit fade
    const tick = () => {
      const now = Date.now();
      if (queueLockRef.current) {
        if (!lastQueueLockTrueAt) lastQueueLockTrueAt = now;
        if (now - lastQueueLockTrueAt >= STUCK_THRESHOLD_MS) {
          console.warn('[MusicContext] Stuck queueLockRef held for',
                       Math.round((now - lastQueueLockTrueAt) / 1000),
                       's — force-clearing to unblock crossfades.');
          reportPlaybackStall('queue_lock_stuck_forced_clear', {
            held_for_s: Math.round((now - lastQueueLockTrueAt) / 1000),
          });
          queueLockRef.current = false;
          lastQueueLockTrueAt = 0;
        }
      } else {
        lastQueueLockTrueAt = 0;
      }
      // fadeIntervalRef holds a setInterval handle when active. If a
      // fade choreography's async chain died without calling
      // clearInterval, the ref persists as a truthy handle. We can't
      // safely clear it here without knowing it's the same handle
      // (double-clear is a no-op on most browsers but let's be paranoid),
      // so just log for now — Forensics tells us if it's happening.
      if (fadeIntervalRef.current) {
        if (!lastFadeIntervalTrueAt) lastFadeIntervalTrueAt = now;
        if (now - lastFadeIntervalTrueAt >= STUCK_THRESHOLD_MS) {
          console.warn('[MusicContext] Stuck fadeIntervalRef held for',
                       Math.round((now - lastFadeIntervalTrueAt) / 1000), 's.');
          reportPlaybackStall('fade_interval_stuck', {
            held_for_s: Math.round((now - lastFadeIntervalTrueAt) / 1000),
          });
          try { clearInterval(fadeIntervalRef.current); } catch { /* noop */ }
          fadeIntervalRef.current = null;
          lastFadeIntervalTrueAt = 0;
        }
      } else {
        lastFadeIntervalTrueAt = 0;
      }
    };
    const handle = createBackgroundInterval(tick, 5_000);
    return () => handle.clear();
  }, []);
  // If we've been "playing" the same track for longer than its duration
  // plus a 15s buffer, we missed the ENDED event entirely. Force
  // handleTrackEnded to advance.
  useEffect(() => {
    let currentId = null;
    let startedAt = null;
    let expected = null;
    let lastRecoveryAt = 0;
    let lastCur = null; // last observed currentPlaybackTime (seconds)

    const forceAdvance = (reason, meta) => {
      const now = Date.now();
      if (now - lastRecoveryAt < 30_000) return false;
      lastRecoveryAt = now;
      console.warn('[MusicContext]', reason, '— forcing advance.', meta);
      reportPlaybackStall(reason, meta);
      currentId = null; startedAt = null; expected = null; lastCur = null;
      try { handleTrackEndedRef.current?.(); } catch (_) { /* noop */ }
      return true;
    };

    const tick = () => {
      if (!playIntentRef.current) { currentId = null; startedAt = null; lastCur = null; return; }
      const m = musicRef.current;
      if (!m || m.playbackState !== 2) { currentId = null; startedAt = null; lastCur = null; return; }
      const id = nowPlayingIdRef.current;
      const dur = (() => { try { return Number(m.currentPlaybackDuration); } catch { return null; } })();
      const cur = (() => { try { return Number(m.currentPlaybackTime); } catch { return null; } })();
      if (!id) return;

      if (currentId !== id) {
        // New track — reset timers.
        currentId = id;
        startedAt = Date.now();
        expected = dur && dur > 0 ? dur * 1000 : null;
        lastCur = Number.isFinite(cur) ? cur : null;
        return;
      }
      if (!expected && dur && dur > 0) expected = dur * 1000;

      const netFlaky = isNetworkFlaky();

      // --- Loop-restart detector (Oct 5 2026 — desktop "same song repeats") ---
      // MusicKit can silently loop the current item back to ~0 at track end
      // (single-item queue exhaustion / repeat drift) while keeping the SAME
      // nowPlayingItem id and playbackState===2. The crossfade trigger then
      // sees a huge `remaining` and never fires, so the venue hears the song
      // start over. Detect the backward playhead jump from near-end → ~start
      // and advance within ~2s instead of waiting for the 15s overrun net.
      if (
        expected && Number.isFinite(cur) && Number.isFinite(lastCur) &&
        lastCur * 1000 >= expected - 6000 && // previous tick was within 6s of the end
        cur < 3 &&                            // now restarted near the beginning
        !netFlaky
      ) {
        if (forceAdvance('track_loop_restart_forced_advance', {
          track_id: id,
          looped_from_s: Math.round(lastCur),
          expected_s: Math.round(expected / 1000),
        })) return;
      }
      lastCur = Number.isFinite(cur) ? cur : lastCur;

      if (!expected) return;

      // --- Overrun fallback (missed ENDED event) ---
      // Patient 15s ONLY while the network is flaky (MusicKit may just be
      // re-handshaking DRM after a blip — skipping would drop a good track).
      // When the network is healthy, a track sitting 6s past its own
      // duration with no advance is dead air the guests are hearing right
      // now — recover fast. This is the primary desktop "silent between
      // songs" fix (prod forensics: overrun_s was consistently 15-19s).
      const threshold = netFlaky ? 15_000 : 6_000;
      const now = Date.now();
      const overrun = now - startedAt - expected;
      if (overrun < threshold) return;

      forceAdvance('track_duration_overrun', {
        track_id: id, overrun_s: Math.round(overrun / 1000),
        expected_s: Math.round(expected / 1000),
        network_flaky: netFlaky,
      });
    };

    const handle = createBackgroundInterval(tick, 2_000);
    return () => handle.clear();
  }, [isNetworkFlaky]);

  // === Heartbeat beacon (Feb 6 2026) =====================================
  // Field report: venue reports "app pauses intermittently" but forensics
  // shows no failures. Two possibilities: (a) the stalls exist but the
  // watchdog's `handleTrackEnded` silently recovered before telemetry
  // ever fired — now fixed by adding reports in the watchdog itself — OR
  // (b) the tab isn't reaching our backend AT ALL (offline / SW cache /
  // CSP block).  The heartbeat rules out (b) by pinging /telemetry every
  // 5 minutes with reason `heartbeat_alive` while the DJ intends to play.
  //
  // Also emits a snapshot of current state so we can look at Forensics
  // and see: "app was alive at 9:30 pm, saw isPlaying=false at 9:32 pm,
  // then paused_mid_song at 9:33 pm" — a proper timeline instead of
  // silence.
  useEffect(() => {
    const beat = () => {
      // Only beat when we intend to be playing — a paused tab isn't a
      // problem to report on.
      if (!playIntentRef.current) return;
      const m = musicRef.current;
      reportPlaybackStall('heartbeat_alive', {
        playback_state: m?.playbackState ?? null,
        current_track_id: nowPlayingIdRef.current || null,
        doc_hidden: typeof document !== 'undefined' ? document.hidden : null,
      });
    };
    // Web-worker interval so Android Chrome's 1/min hidden-tab
    // throttling can't silence us — this is precisely the failure mode
    // we're trying to distinguish from real stalls.
    const handle = createBackgroundInterval(beat, 5 * 60 * 1000);
    return () => handle.clear();
  }, []);

  // === Queue-Depth Watchdog (Feb 2026 — anti-slow-path) ==================
  // Root cause of field-reported "gap between songs grows to 3-5s after
  // ~50min of playback" (client: Feb 9): Apple MusicKit's `queue.append`
  // starts silent-failing on long-lived tabs after N tracks (SDK internal
  // state bloat / DRM cache pressure). When ALL three prewarm windows
  // (T-25s / T-15s / T-10s) silent-fail for the same track, the crossfade
  // fast-path (`skipToNextItem`) can't find the next item and falls into
  // the slow-path rebuild — a 4-6s DRM cold-fetch = the audible gap.
  //
  // Existing defense is 3 attempts per track, all clustered in the last
  // 25 seconds. This watchdog runs INDEPENDENTLY every 45s while playing
  // and silently maintains a 2-item queue-ahead depth. That gives Apple's
  // SDK many more retry opportunities during a normal 3-minute song
  // instead of piling attempts into the last 25 seconds.
  //
  // Also emits telemetry: `window.__musicboxQueueHealth = { depth, appendFails, slowPathCount }`
  // — read by the Console health card, and used by the tiered auto-reload
  // to escalate to a forced reset when the queue keeps wedging.
  const queueHealthRef = useRef({ depth: 0, appendFails: 0, slowPathCount: 0, healed: 0 });
  useEffect(() => {
    if (!playerReady) return undefined;
    const tick = async () => {
      const m = musicRef.current;
      if (!m || m.playbackState !== 2 || queueLockRef.current) return;
      let qLen = 0; let qPos = 0;
      try { qLen = m.queue?.length ?? 0; qPos = m.queue?.position ?? 0; } catch { return; }
      const ahead = Math.max(0, qLen - qPos - 1);
      queueHealthRef.current.depth = ahead;
      try {
        window.__musicboxQueueHealth = { ...queueHealthRef.current };
      } catch { /* noop */ }
      if (ahead >= 2) return; // healthy
      // Depth is short — the crossfade would fall to slow-path if it fired
      // right now. Try to append the next backend track silently, using
      // the same verify-postLen pattern as the existing prewarm.
      try {
        const qRes = await axios.get(`${API}/queue/current`);
        const upcoming = (qRes.data?.tracks || []).slice((qRes.data?.current_index ?? 0) + 1, (qRes.data?.current_index ?? 0) + 4);
        if (!upcoming.length) return;
        const resolved = await Promise.all(
          upcoming.map((t) => (looksLikeAppleId(t.id) ? t.id : (t.apple_id || resolveAppleId(t).catch(() => null))))
        );
        const nextIds = resolved.filter(Boolean);
        for (const nid of nextIds.slice(0, 2 - ahead)) {
          const preLen = m.queue?.length ?? 0;
          try {
            if (m.playbackQueue && typeof m.playbackQueue.append === 'function') {
              await m.playbackQueue.append({ song: nid });
            } else if (m.queue && typeof m.queue.append === 'function') {
              await m.queue.append({ song: nid });
            }
          } catch (_) { /* fall through to verification */ }
          const postLen = m.queue?.length ?? 0;
          if (postLen > preLen) {
            queueHealthRef.current.appendFails = 0;
            queueHealthRef.current.healed += 1;
            console.info('[QueueWatchdog] healed depth', preLen, '→', postLen, 'via silent append of', nid);
          } else {
            queueHealthRef.current.appendFails += 1;
            console.warn('[QueueWatchdog] append silent-fail — depth stuck at', preLen, '(fails:', queueHealthRef.current.appendFails, ')');
            // 3 consecutive silent-fails = MusicKit's queue is wedged.
            // Stop hammering it — the next crossfade will slow-path anyway
            // and the auto-reset will pick it up when idle. Break out of
            // this loop so we don't burn CPU on doomed appends.
            if (queueHealthRef.current.appendFails >= 3) break;
          }
        }
      } catch (e) {
        console.warn('[QueueWatchdog] tick error:', e?.message);
      }
    };
    // Run once immediately after mount so a freshly-loaded session doesn't
    // have to wait 45s before the first depth check.
    tick();
    // Web-Worker-backed interval so a backgrounded tab (Android Chrome
    // screen-off) still gets depth checks at the intended 45s cadence
    // instead of the ~60s+ throttle Chrome applies to plain setInterval.
    const handle = createBackgroundInterval(tick, 45_000);
    return () => handle.clear();
  }, [playerReady]);

  // === DRM session auto-recovery (Android Chrome Widevine resilience) ===
  // Android Chrome's Widevine CDM occasionally drops mid-playback on power-save,
  // network changes, or multitasking — especially on cheaper Android tablets.
  // Venues can't be tapping Retry buttons, so we silently re-attempt playback
  // with exponential backoff: 2s → 5s → 10s → 20s → 40s → 60s → 90s → 120s.
  // ~95% of drops self-heal within the first three attempts. Only after ALL
  // eight attempts fail (total ~6 min of FOREGROUND time) does the user-facing
  // banner surface — by then it's a real device-level problem worth escalating.
  //
  // Multitasking-aware: we DON'T burn an attempt while the page is hidden
  // (m.play() can't succeed in background anyway). And we only kick off the
  // chain at all if the host actually intended to be playing — opportunistic
  // CDM errors fired by MusicKit during random user interactions (clicking a
  // playlist, scrolling, etc.) used to start a useless retry chain that
  // eventually surfaced the banner on screens where playback was never even
  // attempted.
  useEffect(() => {
    const delays = [2000, 5000, 10000, 20000, 40000, 60000, 90000, 120000];
    let retryTimer = null;
    let attempt = 0;
    const tryResume = async () => {
      // If the user isn't trying to play right now, drop the whole chain
      // silently — no point recovering nothing.
      if (!playIntentRef.current) {
        attempt = 0;
        return;
      }
      // Browser will refuse play() while page is hidden; wait for visibility
      // instead of burning this attempt.
      if (typeof document !== 'undefined' && document.hidden) {
        const onVis = () => {
          if (!document.hidden) {
            document.removeEventListener('visibilitychange', onVis);
            // Restart at the SAME attempt index now that we're foregrounded.
            tryResume();
          }
        };
        document.addEventListener('visibilitychange', onVis);
        return;
      }
      const m = musicRef.current;
      if (!m) { dispatchDrmBanner(); return; }
      if (m.playbackState === 2) { attempt = 0; return; } // already recovered
      try {
        await m.play();
        // playbackStateDidChange will flip isPlaying back to true.
        attempt = 0;
      } catch (err) {
        attempt++;
        if (attempt < delays.length) {
          retryTimer = setTimeout(tryResume, delays[attempt]);
        } else {
          dispatchDrmBanner();
          attempt = 0;
        }
      }
    };
    const dispatchDrmBanner = () => {
      // Only escalate to the user banner if the host was actively trying to
      // play. Otherwise the DRM hiccup is irrelevant noise.
      if (!playIntentRef.current) return;
      try {
        window.dispatchEvent(new CustomEvent('spin:appleMusic:drmUnavailable', {
          detail: { message: 'DRM playback could not be recovered after 8 attempts' },
        }));
      } catch { /* noop */ }
    };
    const onDrmHint = () => {
      // Opportunistic DRM errors fire CONSTANTLY on cheap Android Chrome
      // tablets (CDM negotiation, MediaKeys lifecycle, EME setup) — many
      // happen even when the host isn't playing anything yet. Ignore those.
      if (!playIntentRef.current) return;
      if (retryTimer) clearTimeout(retryTimer);
      attempt = 0;
      retryTimer = setTimeout(tryResume, delays[0]);
    };
    window.addEventListener('spin:appleMusic:drmRecoveryNeeded', onDrmHint);
    return () => {
      window.removeEventListener('spin:appleMusic:drmRecoveryNeeded', onDrmHint);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, []);

  // Cleanup on logout
  useEffect(() => {
    if (!user) {
      killFade();
      const m = musicRef.current;
      if (m) { try { m.stop?.(); } catch (e) { /* noop */ } }
      setCurrentTrack(null);
      setIsPlaying(false);
      setShowPlayer(false);
      setQueue([]);
      setQueueIndex(0);
      setNextUp(null);
      setTransition(null);
      setSlotATrack(null);
      setSlotBTrack(null);
      setSlotAState('idle');
      setSlotBState('idle');
      queueLockRef.current = false;
      activeSlotRef.current = 'A';
    }
  }, [user, killFade]);

  // === DJ Console: manual slot controls (single-engine adaptation) ===
  const setAutoDJ = useCallback((value) => {
    setAutoDJState(!!value);
    if (!value) {
      killFade();
      queueLockRef.current = false;
      setTransition(null);
    }
  }, [killFade]);

  const setCrossfader = useCallback((value) => {
    const v = Math.max(0, Math.min(1, Number(value) || 0));
    setCrossfaderState(v);
    // Apple Music can't truly cross-channel, so the crossfader is purely
    // visual when Auto DJ is off. We could map it to global volume, but
    // leaving as-is keeps the UX honest.
  }, []);

  // Manual deck "load" — stages a track on the chosen slot and, if it's the
  // active slot, loads it into MusicKit. Otherwise just stored for UI.
  const loadSlot = useCallback(async (slot, track) => {
    if (!track || !track.id) return;
    if (slot === 'A') setSlotATrack(track);
    else setSlotBTrack(track);
    setShowPlayer(true);
    if (slot === activeSlotRef.current) {
      const id = looksLikeAppleId(track.id) ? track.id : await resolveAppleId(track);
      const m = musicRef.current;
      if (m && id) {
        try { await setAppleQueue([id], 0); } catch (e) { console.warn('loadSlot setQueue failed', e?.message); }
      }
      setCurrentTrack(track);
    }
  }, [resolveAppleId, setAppleQueue]);

  const playSlot = useCallback(async (slot) => {
    // In single-engine mode, "play slot" = make slot active + play its track.
    const track = slot === 'A' ? slotATrack : slotBTrack;
    const previousActive = activeSlotRef.current;
    activeSlotRef.current = slot;
    // Immediately flip the previously-active deck's UI to 'paused' so its
    // button shows the play icon (the user's next tap on it should switch
    // active back, not no-op). This avoids the 500ms gap before the polling
    // tick re-syncs and prevents the "stuck on pause" bug.
    if (previousActive !== slot) {
      if (previousActive === 'A') setSlotAState(slotATrack ? 'paused' : 'idle');
      else setSlotBState(slotBTrack ? 'paused' : 'idle');
    }
    if (track) {
      const id = looksLikeAppleId(track.id) ? track.id : await resolveAppleId(track);
      const m = musicRef.current;
      if (m && id) {
        try {
          // If the now-playing track already matches, just resume; else reload.
          if (nowPlayingIdRef.current !== id) {
            await setAppleQueue([id], 0);
          }
          await m.play();
          setCurrentTrack(track);
          setShowPlayer(true);
        } catch (e) { console.warn('playSlot failed', e?.message); }
      }
    }
  }, [slotATrack, slotBTrack, resolveAppleId, setAppleQueue]);

  const pauseSlot = useCallback((slot) => {
    if (slot === activeSlotRef.current) pause();
  }, [pause]);

  const seekSlot = useCallback((slot, seconds) => {
    if (slot !== activeSlotRef.current) return;
    const m = musicRef.current;
    if (!m) return;
    const s = Math.max(0, Number(seconds) || 0);
    try { m.seekToTime?.(s); } catch (e) { /* noop */ }
    if (slot === 'A') setSlotAProgress(s * 1000);
    else setSlotBProgress(s * 1000);
    setProgressMs(s * 1000);
  }, []);

  // Feb 14 2026 — user-facing seek for the Home booth's draggable progress
  // bar. Clamps to [0, duration] and mirrors optimistically into the React
  // progressMs so the UI thumb doesn't visually rubber-band while MusicKit
  // catches up. Guest requests + celebrations still land at the queue
  // boundary — this ONLY moves the head within the current track.
  const seekTo = useCallback((seconds) => {
    const m = musicRef.current;
    if (!m) return;
    const durSec = (durationMs || 0) / 1000;
    const s = Math.max(0, Math.min(Number(seconds) || 0, durSec || Number(seconds) || 0));
    try { m.seekToTime?.(s); } catch (e) { /* noop — SDK not ready yet */ }
    setProgressMs(s * 1000);
  }, [durationMs]);

  return (
    <MusicContext.Provider value={{
      isAuthorized, currentTrack, isPlaying, playerReady,
      progressMs, durationMs, showPlayer, authPromptCallback: null,
      queue, queueIndex, nextUp, transition, crossfadeActive,
      playSong, playTracks, injectNext, pause, play,
      nextTrack, previousTrack, completeAuthAndPlay: () => {},
      dismissAuthPrompt: () => {}, advanceToNext: nextTrack,
      refreshQueue, skipTransition, syncQueue, seekTo,
      // DJ Console
      autoDJ, setAutoDJ, crossfader, setCrossfader,
      slotATrack, slotBTrack, slotAState, slotBState,
      slotAProgress, slotBProgress, slotADuration, slotBDuration,
      activeSlot: activeSlotRef.current,
      loadSlot, playSlot, pauseSlot, seekSlot,
    }}>
      {children}
    </MusicContext.Provider>
  );
};

export const useMusic = () => {
  const context = useContext(MusicContext);
  if (!context) throw new Error('useMusic must be used within a MusicProvider');
  return context;
};
